import { useEffect, useState, type Dispatch, type SetStateAction } from "react";
import { useAnthem } from "@/components/anthem-provider";
import { useBetaToolAccess } from "@/hooks/use-beta-tool-access";
import { DEFAULT_SIDEBAR_WIDTH, useSidebarWidth } from "@/hooks/use-sidebar-width";
import { ImpersonationGuardProvider } from "@/components/impersonation-guard";
import { BirdieWidget } from "@/components/birdie-widget";
import { SavedIndicator } from "@/components/saved-indicator";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation, Link } from "wouter";
import { queryClient, apiRequest, SESSION_REVOKED_STORAGE_KEY } from "@/lib/queryClient";
import { PermissionsProvider } from "@/hooks/use-permissions";
import { FeatureRequestDialog } from "@/components/feature-request-dialog";
import { MarketingNavigation } from "@/components/sidebar-marketing-navigation";
import { Lightbulb, Building2, MessageSquare, DollarSign, Settings, LogOut, Search, ClipboardList, ImageIcon, Users, UserCheck, Wrench, Wifi, WifiOff, ChevronDown, ChevronRight, ChevronLeft, Lock, Thermometer, Bell, X, Check, Briefcase, ClipboardCheck, CreditCard, BarChart3, Receipt, Settings2, RefreshCw, User, Gift, Menu, Package, PackageX, Zap, LayoutDashboard, Sofa, Rocket, Hammer, AlertCircle, AlertTriangle, Car, Megaphone, Globe, ListChecks, Home, TrendingUp, LayoutList, Landmark, KeyRound, Target, FileText, Wallet, Tag, HelpCircle, Star, BookOpen, AtSign, CalendarClock, Reply, CalendarCheck, ShoppingBag, UserPlus, BatteryLow, Code2, ScrollText, GripVertical, LayoutGrid, Activity, LineChart, Trash2, Handshake, FileWarning, ListTodo, Play, Pause, FlaskConical, Sparkles, Bot } from "lucide-react";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { TruncatedText } from "@/components/truncated-text";
import logoImg from "@assets/Padpulse_logo_horizontal_nobg.png";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import type { AppArea, AreaPermissions, Notification as AppNotification, AccountType } from "@shared/schema";
import { hasFeatureAccess, MENU_TIER_LOCK_HREF_TO_KEY, planDisplayName } from "@shared/schema";
import { bellNotifications } from "@/lib/bell-notifications";
import { resolveRouteMenuLock } from "@/lib/upgrade-info";
import { UpgradeModal, UpgradeContent } from "@/components/upgrade-modal";
import { UpgradeRequiredListener } from "@/components/upgrade-required-listener";
import { V3BillingBanner, V3LifetimeBadge } from "@/components/billing/v3-billing-banner";
import { V3BillingRequiredListener } from "@/components/billing/v3-billing-required-listener";
import { useIsV3Billing } from "@/hooks/use-v3-billing";
import {
  SubscriptionInactiveBanner,
  SubscriptionInactiveScreen,
  isAvailableWhileInactive,
} from "@/components/subscription-inactive-notice";
import { inactiveSubscriptionReason, needsPaymentSetupFromResponse } from "@shared/subscription-gate";
import { useBetaAccess, useBetaAccessKeys } from "@/hooks/use-beta-access";
import { privateBetaForPath } from "@shared/private-property-betas";
import { isHiddenAppPath } from "@shared/hidden-sections";
import { useAppFlags } from "@/hooks/use-app-flags";
import {
  useFeatureGates,
  gateBlockForRoute,
  navHiddenFor,
  isSectionNavHidden,
  comingSoonBadgeForNav,
} from "@/hooks/use-feature-gates";
import { ComingSoon } from "@/components/coming-soon";
import NotFoundPage from "@/pages/not-found";
import { FinishSetupBanner } from "@/components/finish-setup-banner";
import { StagingBanner } from "@/components/staging-banner";
import { PrivacyPolicyNotice } from "@/components/privacy-policy-notice";
import { PromiseTrustBanner } from "@/components/promise-trust-banner";
import { GetStartedLauncher } from "@/components/get-started-launcher";
import { SidebarSearch } from "@/components/sidebar-search";
import { useCrispIdentity } from "@/hooks/use-crisp-identity";
import { useOneSignalIdentity, logoutOneSignalIdentity } from "@/hooks/use-onesignal-identity";
import { stashReturnTo, clearReturnTo } from "@/lib/auth-return-to";
import { analyticsAccountId, identifyAppAccount, resetAppAnalytics } from "@/lib/app-analytics";
import { useReplayPrivacy } from "@/lib/replay-privacy";
import { format } from "date-fns";

type NavItem = {
  label: string;
  href: string;
  icon: typeof Building2;
  nested?: boolean;
  subNested?: boolean;
  area?: AppArea;
  alwaysShow?: boolean;
};

const navItems: NavItem[] = [
  { label: "Booking Requests", href: "/app/members/booking-requests", icon: ClipboardList, area: "members" },
  { label: "Messages", href: "/app/messages", icon: MessageSquare, area: "messages" },
];

type SidebarItemMeta = { label: string; icon: typeof Building2 };

const SIDEBAR_ITEM_REGISTRY: Record<string, SidebarItemMeta> = {
  "/app/daily-pulse": { label: "My Daily Pulse", icon: Activity },
  "/app/dashboard": { label: "Home", icon: Home },
  "/app/analytics": { label: "Portfolio Analytics", icon: TrendingUp },
  "/app/properties": { label: "Properties", icon: LayoutList },
  "/app/messages": { label: "Messages", icon: MessageSquare },
  "/app/members": { label: "Members", icon: UserCheck },
  "/app/members/vehicles": { label: "Vehicles", icon: Car },
  "/app/members/booking-requests": { label: "Booking Requests", icon: ClipboardList },
  "/app/research": { label: "Research", icon: TrendingUp },
  "/app/buy-box/settings": { label: "Buy Box Settings", icon: Settings2 },
  "/app/buy-box/reports": { label: "Buy Box Reports", icon: FileText },
  "/app/buy-box/saved": { label: "Buy Box Saved", icon: Star },
  "/app/buy-box/agents": { label: "Buy Box Agents", icon: Bot },
  "/app/underwriting": { label: "Underwrite", icon: ClipboardList },
  "/app/acquisitions": { label: "Acquisitions", icon: KeyRound },
  "/app/find-realtor": { label: "Find a Realtor", icon: UserCheck },
  "/app/finance": { label: "Get Financing", icon: Landmark },
  "/app/connections": { label: "Connections", icon: Handshake },
  "/app/resources/mercury": { label: "Earn $250", icon: Landmark },
  "/app/buy": { label: "Buy", icon: Home },
  "/app/renovations": { label: "Renovate", icon: Hammer },
  "/app/furnishing-catalog": { label: "Furnish", icon: Sofa },
  "/app/property-launch": { label: "Launch", icon: Rocket },
  "/app/sell": { label: "Sell", icon: Tag },
  "/app/pricing": { label: "Pricing / Rooms", icon: DollarSign },
  "/app/prompt-library": { label: "Prompts & Settings", icon: BookOpen },
  "/app/marketing/direct-bookings": { label: "Direct Bookings", icon: Globe },
  "/app/marketing/third-party-listings": { label: "Third Party Listings", icon: ListChecks },
  "/app/tasks": { label: "Tasks", icon: ClipboardCheck },
  "/app/operations/task-rules": { label: "Task Rules", icon: Zap },
  "/app/reviews": { label: "Reviews", icon: Star },
  "/app/operations/service-forms": { label: "Forms", icon: ClipboardList },
  "/app/cleaning": { label: "Cleaning Program", icon: Sparkles },
  "/app/supplies": { label: "Supplies", icon: Package },
  "/app/account/knowledge-base": { label: "Knowledge Base", icon: BookOpen },
  "/app/operations/vendors": { label: "Vendor Directory", icon: Users },
  "/app/operations/approvals": { label: "Vendor Payments", icon: CreditCard },
  "/app/operations/marketplace": { label: "Vendor Marketplace", icon: Users },
  "/app/operations/vendors/settings": { label: "Vendor Settings", icon: Settings },
  "/app/operations/owners": { label: "Owners Directory", icon: Handshake },
  "/app/operations/trash": { label: "Trash Service", icon: Trash2 },
  "/app/operations/owner-invoices": { label: "Owner Invoices", icon: FileText },
  "/app/account/pm-billing": { label: "Owner Settings", icon: DollarSign },
  "/app/financials": { label: "Financials", icon: BarChart3 },
  "/app/financials/income": { label: "Income", icon: Receipt },
  "/app/financials/expenses": { label: "Expenses", icon: Receipt },
  "/app/financials/settings": { label: "Financials Settings", icon: Settings2 },
  "/app/financials/collections": { label: "Collections", icon: AlertCircle },
  "/app/financials/mail-inbox": { label: "Forwarded Docs", icon: AtSign },
  "/app/devices": { label: "Devices Overview", icon: Wifi },
  "/app/devices/smart-locks": { label: "Smart Locks", icon: Lock },
  "/app/devices/thermostats": { label: "Thermostats", icon: Thermometer },
  "/app/team": { label: "My Team", icon: Users },
  "/app/team-stats": { label: "Team Stats", icon: BarChart3 },
  "/app/account/billing": { label: "Billing", icon: Wallet },
  "/app/profile": { label: "Profile & Account Settings", icon: Settings2 },
  "/app/padsplit-sync": { label: "PadSplit Sync", icon: Zap },
};

function FounderBadge({ user, inheritedFounderTier, inheritedFounderStatus, planInviterName }: { user: { selectedPlan?: string; founderTier?: string | null; founderStatus?: string | null } | undefined; inheritedFounderTier?: string | null; inheritedFounderStatus?: string | null; planInviterName?: string | null }) {
  if (!user) return null;
  const plan = user.selectedPlan || "";
  const isFounderOwner = plan.startsWith("Founder") && user.founderStatus === "active";
  const isInheritedFounder = !isFounderOwner && inheritedFounderStatus === "active";
  if (!isFounderOwner && !isInheritedFounder) return null;
  const tier = isFounderOwner ? user.founderTier : inheritedFounderTier;
  const titleText = isInheritedFounder
    ? `Founder tier: ${tier}${planInviterName ? ` (via ${planInviterName})` : ""}`
    : tier ? `Founder tier: ${tier}` : "Founder";
  return (
    <span
      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-brand-accent to-[#ff8c00] text-white shadow-sm"
      title={titleText}
      data-testid="badge-founder"
    >
      <svg className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 2l2.39 4.84L20 8.27l-3.86 3.77.91 5.31L12 14.77l-5.05 2.58.91-5.31L4 8.27l5.61-1.43L12 2z" />
      </svg>
      Founder
    </span>
  );
}

type NotificationIconStyle = {
  Icon: typeof Bell;
  iconClass: string;
  bgClass: string;
  label: string;
};

const NOTIFICATION_ICON_STYLES: Record<string, NotificationIconStyle> = {
  thermostat_health: { Icon: Thermometer, iconClass: "text-orange-600", bgClass: "bg-orange-100", label: "Thermostat alert" },
  assignment: { Icon: ClipboardCheck, iconClass: "text-blue-600", bgClass: "bg-blue-100", label: "Assignment" },
  mention: { Icon: AtSign, iconClass: "text-purple-600", bgClass: "bg-purple-100", label: "Mention" },
  maintenance: { Icon: Wrench, iconClass: "text-amber-600", bgClass: "bg-amber-100", label: "Maintenance" },
  due_date: { Icon: CalendarClock, iconClass: "text-rose-600", bgClass: "bg-rose-100", label: "Due date" },
  thread_reply: { Icon: Reply, iconClass: "text-sky-600", bgClass: "bg-sky-100", label: "Reply" },
  booking_request: { Icon: CalendarCheck, iconClass: "text-emerald-600", bgClass: "bg-emerald-100", label: "Booking request" },
  sale_inquiry: { Icon: ShoppingBag, iconClass: "text-indigo-600", bgClass: "bg-indigo-100", label: "Sale inquiry" },
  team_invitation: { Icon: UserPlus, iconClass: "text-teal-600", bgClass: "bg-teal-100", label: "Team invitation" },
  // Lock health alerts (Task #940 / #987): all three share a lock-themed
  // visual identity so a host scanning the inbox can immediately tell
  // "this is about a smart lock" before reading the title. Color carries
  // the urgency — amber for low battery, gray for offline, red for
  // repeated unlock failures (the loudest, most actionable of the three).
  lock_low_battery: { Icon: BatteryLow, iconClass: "text-amber-600", bgClass: "bg-amber-100", label: "Lock low battery" },
  lock_offline: { Icon: WifiOff, iconClass: "text-gray-600", bgClass: "bg-gray-200", label: "Lock offline" },
  lock_unlock_failures: { Icon: KeyRound, iconClass: "text-red-600", bgClass: "bg-red-100", label: "Lock unlock failures" },
  // Task #1106 / #1065: TTLock account-level connection failure. This is a
  // louder problem than a single lock going offline — the host's TTLock link
  // is broken so *every* lock on the account stops responding until they
  // re-authorize. Render with a warning triangle (not a lock icon) to set it
  // apart from per-device lock alerts above, and keep it red because guests
  // can't unlock the door until the host acts.
  ttlock_connection_broken: { Icon: AlertTriangle, iconClass: "text-red-600", bgClass: "bg-red-100", label: "Smart-lock connection broken" },
  // Task #1149 — Daily admin alert when the monthly city AI refresh cron
  // appears stuck (any cached row older than 35 days). Yellow because it's
  // a "the cron skipped some cities" warning, not a "users are seeing
  // broken pages" outage — admins can re-run the batch from /admin.
  city_ai_analysis_stale: { Icon: AlertTriangle, iconClass: "text-yellow-600", bgClass: "bg-yellow-100", label: "City AI refresh stuck" },
  // Task #3588 — Recurring reminder to upload the closing statement (HUD/ALTA)
  // for a closed acquisition. Amber document-warning icon because the deal is
  // done but the file is missing — not an outage, but still actionable.
  closing_statement_reminder: { Icon: FileWarning, iconClass: "text-amber-600", bgClass: "bg-amber-100", label: "Closing statement missing" },
  // Task #3596 — Admin alert when a new host's starter content (booking
  // template / account defaults / furnishing catalog) failed to seed during
  // signup. Yellow because it's a "this host's account is incomplete, re-run
  // the seeds" warning, not a live outage; deep-links to the App Users tab.
  starter_seed_failed: { Icon: PackageX, iconClass: "text-yellow-600", bgClass: "bg-yellow-100", label: "Starter setup incomplete" },
};

// Notification types that should surface an inline "View lock" CTA in the
// dropdown row. The whole row is already clickable (it deep-links to
// `/app/devices/smart-locks?deviceId=…` via Task #897), but a visible CTA
// makes the next step obvious for hosts who are scanning fast.
const LOCK_NOTIFICATION_TYPES = new Set<string>([
  "lock_low_battery",
  "lock_offline",
  "lock_unlock_failures",
]);

const DEFAULT_NOTIFICATION_ICON_STYLE: NotificationIconStyle = {
  Icon: Bell,
  iconClass: "text-gray-600",
  bgClass: "bg-gray-100",
  label: "Notification",
};

function getNotificationIconStyle(type: string | null | undefined): NotificationIconStyle {
  if (!type) return DEFAULT_NOTIFICATION_ICON_STYLE;
  return NOTIFICATION_ICON_STYLES[type] ?? DEFAULT_NOTIFICATION_ICON_STYLE;
}

type NotificationGroup = {
  key: string;
  latest: AppNotification;
  items: AppNotification[];
  unreadCount: number;
};

// Notification types whose repeated entries (same target link) should collapse
// into a single grouped row in the dropdown. Keeps long-running incidents
// from flooding the inbox with near-duplicate alerts.
// Task #1106: ttlock_connection_broken is also dedup'd per-connection
// server-side (24h window), but a long-running outage will still spawn a
// fresh notification each window. Collapse repeats for the same connection
// so the inbox shows one row per broken account, matching how thermostat
// incidents fold up.
const GROUPED_NOTIFICATION_TYPES = new Set<string>(["thermostat_health", "ttlock_connection_broken"]);

export function groupNotifications(notifs: AppNotification[]): NotificationGroup[] {
  const groups: NotificationGroup[] = [];
  const indexByKey = new Map<string, number>();
  for (const n of notifs) {
    const groupable = n.type && GROUPED_NOTIFICATION_TYPES.has(n.type) && n.link;
    const key = groupable ? `${n.type}::${n.link}` : `single::${n.id}`;
    const existing = indexByKey.get(key);
    if (existing != null) {
      const g = groups[existing];
      g.items.push(n);
      if (!n.read) g.unreadCount++;
      // notifs come back ordered desc by createdAt; latest stays whichever
      // has the newer createdAt regardless of input order.
      if (new Date(n.createdAt).getTime() > new Date(g.latest.createdAt).getTime()) {
        g.latest = n;
      }
    } else {
      indexByKey.set(key, groups.length);
      groups.push({ key, latest: n, items: [n], unreadCount: n.read ? 0 : 1 });
    }
  }
  return groups;
}

// Pure helper for the dropdown's expand/collapse state. Operators can pop
// open a grouped row to inspect every underlying reading for an incident
// (Task #900) without leaving the page or marking anything read, so the set
// of expanded group keys is just toggled in component state.
function sidebarGroupStorageKey(key: string): string {
  return `padpulse:sidebar:${key}:expanded`;
}

function readSidebarGroupExpanded(key: string): boolean | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(sidebarGroupStorageKey(key));
    if (raw === "true") return true;
    if (raw === "false") return false;
    return null;
  } catch {
    return null;
  }
}

function writeSidebarGroupExpanded(key: string, value: boolean): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(sidebarGroupStorageKey(key), value ? "true" : "false");
  } catch {
    /* ignore quota/SecurityError */
  }
}

function usePersistentSidebarGroup(
  key: string,
  routeDefault: boolean,
): [boolean, Dispatch<SetStateAction<boolean>>] {
  const [expanded, setExpanded] = useState<boolean>(() => {
    const stored = readSidebarGroupExpanded(key);
    return stored === null ? routeDefault : stored;
  });
  useEffect(() => {
    writeSidebarGroupExpanded(key, expanded);
  }, [key, expanded]);
  return [expanded, setExpanded];
}

export function toggleExpandedGroup(prev: ReadonlySet<string>, key: string): Set<string> {
  const next = new Set(prev);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

// Sort a group's underlying notifications most-recent-first so the expanded
// history reads from "now" backwards, matching how the dropdown surfaces the
// latest reading at the top.
export function sortGroupItemsForHistory(items: readonly AppNotification[]): AppNotification[] {
  return [...items].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
}

// Task #934/#978: pull the drift magnitude out of a thermostat_health
// notification so the expanded history can render it as its own scannable
// column ("3°F → 5°F → 8°F").
//
// Source of truth (Task #978): the poller now writes a structured
// `metadata.driftF` alongside the human-readable message, so the UI reads
// the typed number directly via `getNotificationDriftF` below — no regex,
// no coupling to the message wording.
//
// Legacy fallback (Task #934): for rows that landed before the metadata
// column existed, we still parse the stable sentence shape the poller
// emitted in `server/services/thermostat-poller.ts::maybeCreateHealthAlert`
// ("Reading is 4.0°F above/below the … setpoint of …°F"). Returns null
// when the message doesn't look like a drift sentence at all so the UI
// falls back gracefully (an em-dash).
const THERMOSTAT_DRIFT_PATTERN = /Reading is (\d+(?:\.\d+)?)°F (above|below)/i;
export function parseDriftFromMessage(message: string | null | undefined): number | null {
  if (!message) return null;
  const match = THERMOSTAT_DRIFT_PATTERN.exec(message);
  if (!match) return null;
  const value = Number(match[1]);
  if (!Number.isFinite(value)) return null;
  return value;
}

// Task #978: prefer the structured drift on `metadata.driftF` and only
// fall back to message parsing for legacy rows written before the column
// existed. Accepts an unknown-shaped notification so callers don't have
// to narrow themselves; the metadata payload is intentionally loose on
// the wire (jsonb) and we revalidate the one field we care about here.
export function getNotificationDriftF(
  notification: { metadata?: unknown; message?: string | null } | null | undefined,
): number | null {
  if (!notification) return null;
  const meta = notification.metadata as { driftF?: unknown } | null | undefined;
  if (meta && typeof meta === "object") {
    const drift = (meta as { driftF?: unknown }).driftF;
    if (typeof drift === "number" && Number.isFinite(drift)) return drift;
    // Drizzle's jsonb columns can serialize numerics as strings depending
    // on the driver path; tolerate that without losing the structured
    // contract by falling all the way back to the message regex.
    if (typeof drift === "string" && drift.trim() !== "" && Number.isFinite(Number(drift))) {
      return Number(drift);
    }
  }
  return parseDriftFromMessage(notification.message);
}

// Task #1080: pull the full structured drift summary (magnitude + direction +
// setpoint) so the collapsed inbox row can render a one-line badge — e.g.
// "+8.0°F above 72°F" — without the operator having to expand the group.
// Strictly reads the typed `metadata.{driftF,setPointF,driftKind}` payload
// the poller writes; returns null when any of those fields is missing or
// unparseable so the badge renders nothing rather than guessing direction
// from the legacy message regex (which would only ever give us magnitude
// and would be a silent data-quality regression to lean on here).
export function getNotificationDriftSummary(
  notification: { metadata?: unknown } | null | undefined,
): { driftF: number; setPointF: number; direction: "above" | "below" } | null {
  if (!notification) return null;
  const raw = notification.metadata;
  if (!raw || typeof raw !== "object") return null;
  const meta = raw as { driftF?: unknown; setPointF?: unknown; driftKind?: unknown };
  const coerceNumber = (v: unknown): number | null => {
    if (typeof v === "number" && Number.isFinite(v)) return v;
    if (typeof v === "string" && v.trim() !== "" && Number.isFinite(Number(v))) {
      return Number(v);
    }
    return null;
  };
  const driftF = coerceNumber(meta.driftF);
  const setPointF = coerceNumber(meta.setPointF);
  if (driftF == null || setPointF == null) return null;
  // Validate driftKind explicitly rather than defaulting an unknown value
  // to "above": a future schema addition (or a corrupt row) shouldn't be
  // able to silently mislabel direction. Suppress the badge instead.
  if (meta.driftKind !== "above_cool" && meta.driftKind !== "below_heat") return null;
  const direction = meta.driftKind === "below_heat" ? "below" : "above";
  return { driftF, setPointF, direction };
}

// Trend classification for the drift column. "worsening" when the current
// reading drifted further from setpoint than the previous (chronologically
// older) reading, "improving" when it shrank, "steady" when equal, and
// "unknown" when either side has no parseable drift. This is what the UI
// uses to color-code each row so an operator can scan the trend at a glance.
export type DriftTrend = "worsening" | "improving" | "steady" | "unknown";
export function classifyDriftTrend(
  current: number | null,
  previous: number | null,
): DriftTrend {
  if (current == null || previous == null) return "unknown";
  if (current > previous) return "worsening";
  if (current < previous) return "improving";
  return "steady";
}

// Task #977: surface "stuck for X" — the duration between the oldest item in
// a thermostat_health group and now — so on-call doesn't have to mentally
// diff timestamps to know whether an alert is fresh or has been screaming
// for hours/days. The poller refreshes the existing OPEN row's createdAt
// past the dedup window, but a brand-new incident still has its very first
// detection in `group.items` (older read rows survive in the inbox until
// they're cleared), so the oldest createdAt is the right anchor.
export function oldestItemCreatedAt(
  items: readonly { createdAt: Date | string | null | undefined }[],
): Date | null {
  let oldest: Date | null = null;
  for (const item of items) {
    if (!item.createdAt) continue;
    const d = item.createdAt instanceof Date ? item.createdAt : new Date(item.createdAt);
    if (Number.isNaN(d.getTime())) continue;
    if (oldest == null || d.getTime() < oldest.getTime()) oldest = d;
  }
  return oldest;
}

// Format the elapsed time between the first detection and now as a compact
// "Xm" / "Xh" / "Xh Ym" / "Xd Yh" pill. The shape matches the task spec
// ("Stuck for 6h", "Stuck for 6h 12m", "Stuck for 2d 4h"). Negative or zero
// durations collapse to "<1m" so we never render an empty / negative pill
// when the helper is called with a clock that has just ticked.
export function formatStuckDuration(start: Date, now: Date = new Date()): string {
  const diffMs = now.getTime() - start.getTime();
  if (!Number.isFinite(diffMs) || diffMs < 60_000) return "<1m";
  const totalMinutes = Math.floor(diffMs / 60_000);
  if (totalMinutes < 60) return `${totalMinutes}m`;
  const totalHours = Math.floor(totalMinutes / 60);
  const remMinutes = totalMinutes - totalHours * 60;
  if (totalHours < 24) {
    return remMinutes === 0 ? `${totalHours}h` : `${totalHours}h ${remMinutes}m`;
  }
  const totalDays = Math.floor(totalHours / 24);
  const remHours = totalHours - totalDays * 24;
  return remHours === 0 ? `${totalDays}d` : `${totalDays}d ${remHours}h`;
}

// Pull the device id out of the link the poller writes for thermostat_health
// notifications (`/app/devices/thermostats?deviceId=device-a`). Returning
// null on any non-matching link keeps callers from accidentally cross-wiring
// a stuck-duration pill onto an unrelated thermostat row when the link
// shape changes.
const THERMOSTAT_LINK_DEVICE_PATTERN = /[?&]deviceId=([^&#]+)/;
export function extractDeviceIdFromThermostatLink(
  link: string | null | undefined,
): string | null {
  if (!link) return null;
  const match = THERMOSTAT_LINK_DEVICE_PATTERN.exec(link);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

// For a given device, find the oldest createdAt across its currently-open
// (unread) thermostat_health notifications so the dashboard / property
// thermostat cards can show the same "Stuck for X" pill the dropdown does.
// Returns null when the device has no open alert, which is the signal to
// hide the pill entirely (the alert is resolved or has been acknowledged).
// Task #1979: resolve a thermostat deviceId to the best deep-link route at
// click time. Notifications and dashboard rows store device-keyed links;
// when the device currently has a property assignment we route to the
// property's Devices tab (where the per-thermostat trend chart lives) so
// the host lands in the right context. Falls back to the global Thermostats
// page when the device is truly unassigned at click time. We deliberately
// resolve here (not at notification creation) so an alert created while the
// thermostat was orphaned still upgrades the moment the host assigns it.
export async function resolveThermostatRoute(deviceId: string | null | undefined): Promise<string> {
  if (!deviceId) return "/app/devices/thermostats";
  const encoded = encodeURIComponent(deviceId);
  try {
    const assignments = await queryClient.fetchQuery<Array<{ deviceId: string; propertyId: number }>>({
      queryKey: ["/api/app/devices/thermostats/property-assignments"],
      // Force a refetch at click time — the global queryClient defaults to
      // staleTime: Infinity, but the whole point of resolving at click time
      // is to honor assignments made *after* the alert was created. A small
      // staleTime keeps a click burst (e.g. opening the dropdown + clicking
      // a row) from double-fetching.
      staleTime: 10_000,
    });
    const match = assignments?.find((a) => a.deviceId === deviceId);
    if (match) {
      return `/app/properties/${match.propertyId}?tab=devices&deviceId=${encoded}`;
    }
  } catch {
    // network/auth failure — fall through to global page, the host can
    // still navigate manually from there.
  }
  return `/app/devices/thermostats?deviceId=${encoded}`;
}

export function findOpenThermostatStuckSince(
  notifications: readonly {
    type: string | null;
    link: string | null;
    read: boolean | null;
    createdAt: Date | string | null | undefined;
  }[],
  deviceId: string,
): Date | null {
  if (!deviceId) return null;
  const matching = notifications.filter(
    (n) =>
      n.type === "thermostat_health" &&
      n.read === false &&
      extractDeviceIdFromThermostatLink(n.link) === deviceId,
  );
  return oldestItemCreatedAt(matching);
}

export function NotificationGroupRow({
  group,
  isExpanded,
  onActivate,
  onToggleExpanded,
}: {
  group: NotificationGroup;
  isExpanded: boolean;
  onActivate: () => void;
  onToggleExpanded: () => void;
}) {
  const notif = group.latest;
  const style = getNotificationIconStyle(notif.type);
  const NotifIcon = style.Icon;
  const isGrouped = group.items.length > 1;
  const hasUnread = group.unreadCount > 0;
  const historyItems = isGrouped && isExpanded ? sortGroupItemsForHistory(group.items) : [];
  // Task #977 / #1076: show "stuck for X" on thermostat_health rows so
  // on-call can tell at a glance whether an alert is fresh or has been
  // going for hours/days. We anchor on the oldest *unread* item in the
  // group rather than the oldest item overall — a device that flares up
  // multiple times over weeks can leave older read alerts in the inbox,
  // and anchoring on those would falsely report "Stuck for 14d" for an
  // incident that actually started this morning. The dashboard /
  // property-detail cards already use findOpenThermostatStuckSince which
  // applies the same `read === false` filter, so all three surfaces now
  // agree on the duration of the *current* open incident.
  const stuckSince =
    notif.type === "thermostat_health"
      ? oldestItemCreatedAt(group.items.filter((item) => item.read === false))
      : null;
  const stuckLabel = stuckSince ? formatStuckDuration(stuckSince) : null;
  // Task #1080: surface the latest drift magnitude + setpoint right on the
  // collapsed row so on-call doesn't have to expand the group to see how
  // bad the current incident is. Only renders for thermostat_health rows
  // and only when the structured metadata is present (no message-regex
  // fallback — see getNotificationDriftSummary for the rationale).
  const driftSummary =
    notif.type === "thermostat_health" ? getNotificationDriftSummary(notif) : null;
  return (
    <div
      className={`border-b last:border-0 ${hasUnread ? "bg-blue-50 dark:bg-blue-950/40" : ""}`}
      data-testid={isGrouped ? `notification-group-${group.key}` : `notification-item-${notif.id}`}
    >
      <div className="flex items-stretch">
        <button
          className="group flex-1 min-w-0 text-left px-3 py-2.5 hover:bg-gray-50 transition-colors"
          onClick={onActivate}
          data-testid={isGrouped ? `button-notification-group-${group.key}` : `button-notification-item-${notif.id}`}
        >
          <div className="flex items-start gap-2">
            <div
              className={`mt-1 h-2 w-2 rounded-full shrink-0 ${
                hasUnread
                  ? notif.type === "mention"
                    ? "bg-purple-500"
                    : "bg-blue-500"
                  : "bg-transparent"
              }`}
              data-testid={
                hasUnread && notif.type === "mention"
                  ? `notification-mention-unread-dot-${notif.id}`
                  : undefined
              }
            />
            <div
              className={`shrink-0 h-8 w-8 rounded-full flex items-center justify-center ${style.bgClass}`}
              aria-label={style.label}
              title={style.label}
              data-testid={`notification-icon-${notif.type ?? "default"}-${notif.id}`}
            >
              <NotifIcon className={`h-4 w-4 ${style.iconClass}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <p className="text-sm font-medium text-gray-900 truncate">{notif.title}</p>
                {stuckLabel && (
                  <span
                    className="shrink-0 inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 border border-orange-200"
                    title={stuckSince ? `First detected ${format(stuckSince, "MMM d, h:mm a")}` : undefined}
                    aria-label={`Stuck for ${stuckLabel}`}
                    data-testid={`notification-group-stuck-${group.key}`}
                  >
                    Stuck for {stuckLabel}
                  </span>
                )}
                {driftSummary && (
                  <span
                    className="shrink-0 inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200 tabular-nums"
                    title={`Latest reading drifted ${driftSummary.driftF.toFixed(1)}°F ${driftSummary.direction} the ${driftSummary.direction === "above" ? "cooling" : "heating"} setpoint of ${driftSummary.setPointF}°F`}
                    aria-label={`Drift ${driftSummary.driftF.toFixed(1)} degrees Fahrenheit ${driftSummary.direction} setpoint of ${driftSummary.setPointF} degrees Fahrenheit`}
                    data-testid={`notification-group-drift-${group.key}`}
                  >
                    +{driftSummary.driftF.toFixed(1)}°F {driftSummary.direction} {driftSummary.setPointF}°F
                  </span>
                )}
              </div>
              {notif.type === "mention" && notif.message ? (
                // Task #1881 — mention rows already store a short preview of the
                // comment in `message` (e.g. `"Hey @alice, can you look at..."`).
                // Render it italic-styled directly beneath the title so the
                // recipient can decide whether to open the task without
                // navigating into it. Truncation to ~80 chars is already done
                // server-side; line-clamp-2 keeps the row compact.
                <p
                  className="text-xs text-gray-600 italic mt-0.5 line-clamp-2"
                  data-testid={`notification-mention-preview-${notif.id}`}
                >
                  {notif.message}
                </p>
              ) : (
                <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{notif.message}</p>
              )}
              {notif.type && LOCK_NOTIFICATION_TYPES.has(notif.type) && notif.link && (
                <span
                  className="inline-flex items-center gap-0.5 mt-1 text-xs font-medium text-blue-600 group-hover:underline"
                  data-testid={`notification-cta-view-lock-${notif.id}`}
                >
                  View lock
                  <ChevronRight className="h-3 w-3" aria-hidden="true" />
                </span>
              )}
              <p className="text-xs text-gray-400 mt-1">
                {isGrouped
                  ? `${group.items.length} alerts · latest ${format(new Date(notif.createdAt), "MMM d, h:mm a")}`
                  : format(new Date(notif.createdAt), "MMM d, h:mm a")}
              </p>
            </div>
          </div>
        </button>
        {isGrouped && (
          <button
            type="button"
            className="shrink-0 px-2 flex items-center gap-1 border-l border-gray-200 hover:bg-gray-100 transition-colors text-gray-600"
            onClick={onToggleExpanded}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? `Collapse history for ${notif.title}` : `Expand history for ${notif.title}`}
            title={isExpanded ? "Hide history" : "Show every reading"}
            data-testid={`button-toggle-notification-group-${group.key}`}
          >
            <span
              className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-700"
              data-testid={`notification-group-count-${group.key}`}
            >
              {group.items.length}
            </span>
            <ChevronDown
              className={`h-4 w-4 transition-transform ${isExpanded ? "rotate-180" : ""}`}
              aria-hidden="true"
            />
          </button>
        )}
      </div>
      {isGrouped && isExpanded && (
        <ul
          className="bg-gray-50 border-t border-gray-200 px-3 py-2 space-y-1"
          data-testid={`notification-group-history-${group.key}`}
        >
          {historyItems.map((item, idx) => {
            // Compare each row to the chronologically older reading
            // (historyItems is most-recent-first, so the older one is at
            // idx + 1) so worsening/improving is decided against the
            // previous data point — that's what makes a 3 → 5 → 8°F trend
            // legible at a glance in the dropdown.
            const drift = getNotificationDriftF(item);
            const previousDrift = idx + 1 < historyItems.length
              ? getNotificationDriftF(historyItems[idx + 1])
              : null;
            const trend = classifyDriftTrend(drift, previousDrift);
            const driftClass =
              trend === "worsening"
                ? "text-red-600 font-semibold"
                : trend === "improving"
                ? "text-green-600 font-semibold"
                : "text-gray-600";
            const driftLabel =
              trend === "worsening"
                ? "Drift increased from previous reading"
                : trend === "improving"
                ? "Drift decreased from previous reading"
                : trend === "steady"
                ? "Drift unchanged from previous reading"
                : "Drift trend unavailable";
            return (
              <li
                key={item.id}
                className="text-xs text-gray-700 flex items-start gap-2"
                data-testid={`notification-group-item-${item.id}`}
              >
                <span className="shrink-0 text-gray-400 tabular-nums w-[88px]">
                  {format(new Date(item.createdAt), "MMM d, h:mm a")}
                </span>
                <span
                  className={`shrink-0 tabular-nums w-[56px] text-right ${driftClass}`}
                  title={driftLabel}
                  aria-label={drift != null ? `Drift ${drift.toFixed(1)} degrees Fahrenheit. ${driftLabel}` : "Drift unavailable"}
                  data-testid={`notification-group-item-drift-${item.id}`}
                  data-drift-trend={trend}
                >
                  {drift != null ? `+${drift.toFixed(1)}°F` : "—"}
                </span>
                <span className="flex-1 break-words">{item.message}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function NotificationHeader({ user, planLabel, planInherited, planInviterName, inheritedFounderTier, inheritedFounderStatus, onLogout }: { user: { name?: string | null; email: string; firstName?: string | null; profilePictureUrl?: string | null; selectedPlan?: string; founderTier?: string | null; founderStatus?: string | null } | undefined; planLabel?: string; planInherited?: boolean; planInviterName?: string | null; inheritedFounderTier?: string | null; inheritedFounderStatus?: string | null; onLogout: () => void }) {
  const [showPanel, setShowPanel] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [location, navigate] = useLocation();
  // The anthem button is only visible on the dashboard tab (per explicit user
  // request). The audio element + playback state live in AnthemProvider, mounted
  // ABOVE the router, so a track started on the dashboard keeps playing as the
  // host navigates elsewhere — they just can't see the pause control until they
  // return to the dashboard. (Each page renders its own AppLayout/topbar, so the
  // audio cannot live here or it would stop on every navigation.)
  const isDashboardPage = location === "/app" || location === "/app/dashboard";
  const {
    isPlaying: anthemPlaying,
    hasInteracted: anthemInteracted,
    toggle: handleAnthemToggle,
    dismissed: anthemDismissed,
    dismiss: handleAnthemDismiss,
  } = useAnthem();

  const countQuery = useQuery<{ count: number; mentionCount?: number; bellCount?: number }>({
    queryKey: ["/api/app/notifications/count"],
    refetchInterval: 30000,
  });

  const notificationsQuery = useQuery<AppNotification[]>({
    queryKey: ["/api/app/notifications"],
    enabled: showPanel,
  });

  const markReadMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (id: number) => {
      await apiRequest("PATCH", `/api/app/notifications/${id}/read`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/notifications/count"] });
    },
  });

  const markBatchReadMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (ids: number[]) => {
      await apiRequest("PATCH", "/api/app/notifications/read-batch", { ids });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/notifications/count"] });
    },
  });

  // PadSplit send-UX — the bell surfaces @-mentions AND terminally-failed sends
  // (bellCount, falling back to mentionCount for stale clients). Both the list
  // and the badge use the shared bellNotifications() set so they can't drift.
  const unreadBellCount = countQuery.data?.bellCount ?? countQuery.data?.mentionCount ?? 0;
  const bellList = bellNotifications(notificationsQuery.data || []);
  const notifGroups = groupNotifications(bellList);
  const unreadBellIds = bellList.filter(n => !n.read).map(n => n.id);

  async function handleGroupClick(group: NotificationGroup) {
    // Task #3588 — closing_statement_reminder notifications are non-dismissible:
    // they must stay unread until the host uploads the HUD or the cron stops
    // generating them. Clicking still navigates to the acquisition page.
    const isNonDismissible = group.latest.type === "closing_statement_reminder";
    if (!isNonDismissible) {
      const unreadIds = group.items.filter(n => !n.read).map(n => n.id);
      if (unreadIds.length > 0) {
        if (unreadIds.length === 1) {
          markReadMutation.mutate(unreadIds[0]);
        } else {
          markBatchReadMutation.mutate(unreadIds);
        }
      }
    }
    // Task #1979: thermostat_health rows always resolve through the
    // assignment lookup so a click lands on the property Devices tab
    // (where the trend chart lives) even when the stored link still
    // points at the global Thermostats fallback from an orphan-at-creation
    // alert. Other notification types navigate to their stored link as-is.
    if (group.latest.type === "thermostat_health") {
      const deviceId = extractDeviceIdFromThermostatLink(group.latest.link);
      const target = deviceId
        ? await resolveThermostatRoute(deviceId)
        : group.latest.link;
      if (target) {
        navigate(target);
        setShowPanel(false);
      }
      return;
    }
    if (group.latest.link) {
      navigate(group.latest.link);
      setShowPanel(false);
    }
  }

  function handleToggleGroupExpanded(key: string) {
    setExpandedGroups(prev => toggleExpandedGroup(prev, key));
  }

  return (
    <header className="h-14 shrink-0 border-b border-gray-200 grid grid-cols-[1fr_auto_1fr] items-center px-6 bg-white" data-testid="app-topbar">
      <div />
      <div className="flex justify-center">
        {isDashboardPage && !anthemDismissed && (
          <div className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-brand-accent pl-5 pr-1.5 py-0.5 text-xs font-semibold text-black shadow-sm transition-all hover:shadow-md">
            <button
              data-testid="button-anthem-toggle"
              onClick={handleAnthemToggle}
              className="inline-flex items-center gap-2 py-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-1"
              aria-label={anthemPlaying ? "Pause PadPulse Anthem" : "Play PadPulse Anthem"}
            >
              {anthemPlaying ? (
                <Pause className="h-3.5 w-3.5 shrink-0 text-black" />
              ) : (
                <Play className="h-3.5 w-3.5 shrink-0 text-black" />
              )}
              <span>
                {!anthemInteracted
                  ? "Want to start your day off right? Listen to the PadPulse Anthem, Heartbeat of the Hustle."
                  : anthemPlaying
                  ? "Pause"
                  : "Play"}
              </span>
            </button>
            <button
              data-testid="button-anthem-dismiss"
              onClick={handleAnthemDismiss}
              className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-black/70 transition-colors hover:bg-black/10 hover:text-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-1"
              aria-label="Hide the PadPulse Anthem button"
              title="Don't show again"
            >
              <X className="h-3.5 w-3.5 shrink-0" />
            </button>
          </div>
        )}
      </div>
      <div className="flex items-center gap-4 justify-end">
        {/* HOST APP ONLY — this pill must not appear in the vendor or partner
            portals. Vendor-portal mutations are silenced via silentSave: true.
            The partner portal has its own isolated QueryClient (partner-main.tsx)
            with no MutationCache hook, so it can never trigger this indicator. */}
        <SavedIndicator />
        <FounderBadge user={user as any} inheritedFounderTier={inheritedFounderTier} inheritedFounderStatus={inheritedFounderStatus} planInviterName={planInviterName} />
        {user && <V3LifetimeBadge />}
        {planLabel && (
          <span
            className={`hidden sm:inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${planInherited ? "bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800" : "bg-gray-100 text-gray-700"}`}
            data-testid="badge-plan-status"
            title={planInherited && planInviterName ? `Plan inherited from ${planInviterName}` : undefined}
          >
            {planLabel}
            {planInherited && planInviterName && (
              <span className="text-blue-600">— via {planInviterName}</span>
            )}
          </span>
        )}
        <div className="relative">
          <button
            className="relative p-2 rounded-full hover:bg-gray-100 transition-colors"
            onClick={() => setShowPanel(!showPanel)}
            data-testid="button-notifications"
          >
            <Bell className="h-5 w-5 text-gray-600" />
            {unreadBellCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 h-5 min-w-[20px] px-1 rounded-full bg-purple-500 text-white text-xs font-medium flex items-center justify-center" data-testid="badge-notification-count">
                {unreadBellCount > 99 ? "99+" : unreadBellCount}
              </span>
            )}
          </button>

          {showPanel && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowPanel(false)} />
              <div className="ph-mask absolute right-0 top-full mt-2 w-80 bg-white border rounded-lg shadow-xl z-50 max-h-[70vh] flex flex-col" data-testid="notification-panel">
                <div className="flex items-center justify-between p-3 border-b">
                  <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
                  <div className="flex items-center gap-1">
                    {unreadBellIds.length > 0 && (
                      <button
                        className="text-xs text-blue-600 hover:underline px-1"
                        onClick={() => markBatchReadMutation.mutate(unreadBellIds)}
                        data-testid="button-mark-all-read"
                      >
                        Mark all read
                      </button>
                    )}
                    <button
                      className="p-1 hover:bg-gray-100 rounded"
                      onClick={() => setShowPanel(false)}
                    >
                      <X className="h-4 w-4 text-gray-400" />
                    </button>
                  </div>
                </div>
                <div className="overflow-y-auto flex-1">
                  {notifGroups.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No notifications yet</p>
                    </div>
                  ) : (
                    notifGroups.map(group => (
                      <NotificationGroupRow
                        key={group.key}
                        group={group}
                        isExpanded={expandedGroups.has(group.key)}
                        onActivate={() => handleGroupClick(group)}
                        onToggleExpanded={() => handleToggleGroupExpanded(group.key)}
                      />
                    ))
                  )}
                </div>
              </div>
            </>
          )}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="h-8 w-8 rounded-full bg-brand flex items-center justify-center text-white text-sm font-medium cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2 hover:opacity-90 transition-opacity overflow-hidden" data-testid="avatar-user">
              {user?.profilePictureUrl ? (
                <img src={user.profilePictureUrl} alt="Profile" className="h-full w-full object-cover" />
              ) : (
                (user?.firstName?.[0] || user?.name?.[0] || user?.email?.[0] || "U").toUpperCase()
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem asChild className="cursor-pointer" data-testid="menu-my-profile">
              <Link href="/app/profile">
                <User className="h-4 w-4 mr-2" />
                My Profile
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild className="cursor-pointer" data-testid="menu-refer-host">
              <Link href="/app/refer">
                <Gift className="h-4 w-4 mr-2" />
                Refer a Host
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onLogout} className="cursor-pointer text-red-600 focus:text-red-600" data-testid="menu-logout">
              <LogOut className="h-4 w-4 mr-2" />
              Log Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

function SortableFavoriteItem({
  href,
  label,
  Icon,
  isActive,
  onUnfavorite,
}: {
  href: string;
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
  isActive: boolean;
  onUnfavorite: (href: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: href });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group flex items-center"
      data-testid={`row-favorite${href.replace(/\//g, "-")}`}
    >
      <button
        type="button"
        className="pl-2 pr-1 py-2 shrink-0 cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus:opacity-100 touch-none"
        title="Drag to reorder"
        aria-label={`Drag to reorder ${label}`}
        data-testid={`drag-favorite${href.replace(/\//g, "-")}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-3.5 w-3.5" />
      </button>
      <Link href={href} className="flex-1 min-w-0">
        <span
          className={`flex items-center gap-3 pl-2 pr-1 py-2 text-sm cursor-pointer ${
            isActive ? "text-gray-900 font-medium bg-gray-50" : "text-gray-600 hover:bg-gray-50"
          }`}
          data-testid={`link-favorite${href.replace(/\//g, "-")}`}
        >
          <Icon className="h-4 w-4 shrink-0" />
          <TruncatedText text={label} className="flex-1" />
        </span>
      </Link>
      <button
        className="pr-3 py-2 shrink-0"
        onClick={() => onUnfavorite(href)}
        title="Remove from favorites"
        data-testid={`btn-unfavorite${href.replace(/\//g, "-")}`}
      >
        <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
      </button>
    </div>
  );
}

function FavoriteStar({ href, favorites, onToggle }: { href: string; favorites: string[]; onToggle: (href: string) => void }) {
  const isFav = favorites.includes(href);
  return (
    <button
      className={`shrink-0 p-0.5 rounded transition-opacity ${isFav ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggle(href); }}
      title={isFav ? "Remove from favorites" : "Add to favorites"}
      data-testid={`btn-favorite${href.replace(/\//g, "-")}`}
    >
      <Star className={`h-3 w-3 ${isFav ? "fill-yellow-400 text-yellow-400" : "text-gray-300 hover:text-yellow-400"}`} />
    </button>
  );
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

let _desktopSidebarOpen = true;

interface SidebarNavItem {
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  gate: () => boolean;
  testid?: string;
  testidCollapsed?: string;
  /** Render nested one level under the item above it (e.g. Task Rules under Tasks). */
  indent?: boolean;
  /** Highlight a resource offer without changing its access rules. */
  highlighted?: boolean;
}

// Maps a sidebar href to its marketing feature page (`/features/*`). Used by
// the locked-submenu "Want to learn more" link so a tier-locked user can read
// about a gated feature before upgrading. Hrefs without an entry simply omit
// the link.
const FEATURE_LEARN_MORE_HREF: Record<string, string> = {
  "/app/members": "/features/members/members",
  "/app/members/vehicles": "/features/members/vehicles",
  "/app/members/booking-requests": "/features/members/booking-requests",
  "/app/reviews": "/features/members/reviews",
  "/app/tasks": "/features/operations/tasks",
  "/app/operations/task-rules": "/features/operations/tasks",
  "/app/operations/service-forms": "/features/operations/service-forms",
  "/app/cleaning": "/features/operations/cleaning-schedule",
  "/app/operations/todos": "/features/operations/todos",
  "/app/supplies": "/features/operations/supplies",
  "/app/operations/trash": "/features/operations/trash-service",
  "/app/pricing": "/features/operations/pricing-rooms",
  "/app/financials": "/features/revenue/profit-loss",
  "/app/financials/income": "/features/revenue/income",
  "/app/financials/expenses": "/features/revenue/expenses",
  "/app/financials/collections": "/features/revenue/collections",
  "/app/sell": "/features/revenue/sell",
  "/app/daily-pulse": "/features/daily-pulse",
};

// Shared renderer for a tier-locked sub-item: shows the item's icon + label
// with an amber "Upgrade" hint and a lock, rendered as a real link to the
// gated page (which shows the upgrade prompt). `variant` matches the two
// sidebar layouts — the collapsed flyout ("popover") and the expanded list
// ("expanded").
function SidebarLockedSubItem({
  label,
  icon: Icon,
  variant,
  href,
  testid,
}: {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  variant: "popover" | "expanded";
  href: string;
  testid: string;
}) {
  const sizing =
    variant === "popover"
      ? "gap-2 px-2 py-1.5 rounded"
      : "gap-3 pl-9 pr-4 py-2";
  const iconSize = variant === "popover" ? "h-3.5 w-3.5" : "h-4 w-4";
  const lockSize = variant === "popover" ? "h-3 w-3" : "h-3.5 w-3.5";
  return (
    <Link
      href={href}
      className={`w-full flex items-center ${sizing} text-sm text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors`}
      data-testid={testid}
    >
      <Icon className={`${iconSize} shrink-0`} />
      <TruncatedText text={label} className="flex-1 text-left" />
      <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-500">Upgrade</span>
      <Lock className={`${lockSize} shrink-0 text-amber-500`} />
    </Link>
  );
}

export default function AppLayout({ children, scrollLocked }: { children: React.ReactNode; scrollLocked?: boolean }) {
  const betaToolAccess = useBetaToolAccess();
  useCrispIdentity();
  useOneSignalIdentity();
  const [location, navigate] = useLocation();
  const isMobile = useIsMobile();
  const sidebarWidth = useSidebarWidth();
  const [sidebarOpen, _setSidebarOpen] = useState(isMobile ? false : _desktopSidebarOpen);

  const setSidebarOpen = (open: boolean) => {
    if (!isMobile) _desktopSidebarOpen = open;
    _setSidebarOpen(open);
  };

  useEffect(() => {
    _setSidebarOpen(isMobile ? false : _desktopSidebarOpen);
  }, [isMobile]);

  useEffect(() => {
    if (isMobile) _setSidebarOpen(false);
  }, [location]);

  const [favoritesExpanded, setFavoritesExpanded] = useState(false);

  const dashPrefsQuery = useQuery<{ sidebarFavorites?: string[] | null } | null>({
    queryKey: ["/api/app/dashboard-preferences"],
  });
  const favorites: string[] = dashPrefsQuery.data?.sidebarFavorites ?? [];

  type DashPrefsCache = { sidebarFavorites?: string[] | null } | null | undefined;
  type FavoritesMutationCtx = { prev: DashPrefsCache };

  const favoritesMutation = useMutation<unknown, Error, string[], FavoritesMutationCtx>({
    mutationFn: async (newFavorites: string[]) => {
      return apiRequest("PATCH", "/api/app/sidebar-favorites", { favorites: newFavorites });
    },
    onMutate: async (newFavorites) => {
      await queryClient.cancelQueries({ queryKey: ["/api/app/dashboard-preferences"] });
      const prev = queryClient.getQueryData<DashPrefsCache>(["/api/app/dashboard-preferences"]);
      queryClient.setQueryData<DashPrefsCache>(["/api/app/dashboard-preferences"], (old) => ({
        ...(old || {}),
        sidebarFavorites: newFavorites,
      }));
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx !== undefined) {
        queryClient.setQueryData(["/api/app/dashboard-preferences"], ctx.prev);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/dashboard-preferences"] });
    },
  });

  function toggleFavorite(href: string) {
    const updated = favorites.includes(href)
      ? favorites.filter((f) => f !== href)
      : [...favorites, href];
    favoritesMutation.mutate(updated);
  }

  const favoritesDragSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const [activeFavoriteId, setActiveFavoriteId] = useState<string | null>(null);

  function handleFavoritesDragStart(event: DragStartEvent) {
    setActiveFavoriteId(String(event.active.id));
  }

  function handleFavoritesDragEnd(event: DragEndEvent, accessibleOrder: string[]) {
    setActiveFavoriteId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = accessibleOrder.indexOf(String(active.id));
    const newIndex = accessibleOrder.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const reorderedAccessible = arrayMove(accessibleOrder, oldIndex, newIndex);
    const accessibleSet = new Set(accessibleOrder);
    let i = 0;
    const updated = favorites.map((href) =>
      accessibleSet.has(href) ? reorderedAccessible[i++] : href,
    );
    favoritesMutation.mutate(updated);
  }

  const isVendorRoute = location.startsWith("/app/operations/vendors") || location.startsWith("/app/operations/approvals") || location.startsWith("/app/operations/marketplace");
  const isBuyBoxRoute = location.startsWith("/app/buy-box");
  const isResourcesRoute =
    location.startsWith("/app/resources/") ||
    location.startsWith("/app/connections") ||
    location.startsWith("/app/find-realtor") ||
    location.startsWith("/app/finance");
  const isOperationsGroupRoute =
    !isVendorRoute && (
      location.startsWith("/app/tasks") ||
      location.startsWith("/app/maintenance") ||
      location.startsWith("/app/operations/service-forms") ||
      location.startsWith("/app/supplies") ||
        location.startsWith("/app/financials/collections")
    );
  // Marketing keeps its own independent dropdown.
  const isMarketingRoute = location.startsWith("/app/marketing");
  const isDevicesGroupRoute = location.startsWith("/app/devices");
  const isOwnersGroupRoute =
    location.startsWith("/app/operations/owners") ||
    location.startsWith("/app/operations/owner-invoices") ||
    location === "/app/account/pm-billing";
  const isRevenueRoute =
    (location.startsWith("/app/financials") && !location.startsWith("/app/financials/collections")) ||
    location.startsWith("/app/pricing") ||
    location.startsWith("/app/prompt-library");
  const isRevenueGroupRoute = isRevenueRoute;
  // Portfolio Analytics is a standalone link below Financials.
  const isAnalyticsRoute = location.startsWith("/app/analytics");

  // Task #1620 — sidebar group expand/collapse is persisted in localStorage.
  // The route-based flag is used only as the first-time default when no
  // stored value exists; once the user manually toggles a group, navigating
  // within that section must NOT override their preference.
  const [buyBoxExpanded, setBuyBoxExpanded] = usePersistentSidebarGroup("buybox", isBuyBoxRoute);
  const [resourcesExpanded, setResourcesExpanded] = usePersistentSidebarGroup("resources", isResourcesRoute);
  const [operationsExpanded, setOperationsExpanded] = usePersistentSidebarGroup("operations", isOperationsGroupRoute);
  const [devicesExpanded, setDevicesExpanded] = usePersistentSidebarGroup("devices", isDevicesGroupRoute);
  const [vendorsExpanded, setVendorsExpanded] = usePersistentSidebarGroup("vendors", isVendorRoute);
  const [ownersExpanded, setOwnersExpanded] = usePersistentSidebarGroup("owners", isOwnersGroupRoute);
  const [marketingExpanded, setMarketingExpanded] = usePersistentSidebarGroup("marketing", isMarketingRoute);

  // Freshness strategy (Task #4144): mirror the Messages badge (Task #4132)
  // so these sibling sidebar badges also self-heal within seconds even when a
  // count changes on a surface that does NOT invalidate their query key, or
  // when the cache was seeded with a stale value before this layout mounted:
  //  - staleTime 0 + refetchOnMount "always": a seeded/cached value is
  //    refetched on mount instead of being trusted forever (the global
  //    queryClient default is staleTime: Infinity).
  //  - refetchOnWindowFocus: catches changes made in another tab/window.
  //  - refetchInterval 10s: tightened from 30s as the worst-case fallback.
  const bookingUnreadQuery = useQuery<{ count: number }>({
    queryKey: ["/api/app/booking-requests/submissions/unread-count"],
    refetchInterval: 10_000,
    refetchOnWindowFocus: true,
    refetchOnMount: "always",
    staleTime: 0,
  });
  const bookingUnreadCount = bookingUnreadQuery.data?.count || 0;

  const connectionsUnreadQuery = useQuery<{ count: number }>({
    queryKey: ["/api/app/connections/unread-count"],
    refetchInterval: 10_000,
    refetchOnWindowFocus: true,
    refetchOnMount: "always",
    staleTime: 0,
  });
  const connectionsUnreadCount = connectionsUnreadQuery.data?.count || 0;

  // Account-level total of open (unresolved) inbound member messages,
  // surfaced as a badge on the "Messages" sidebar row. Equals the sum of
  // per-conversation "N open" badges. Only drops when a message is resolved,
  // never just from reading.
  //
  // Freshness strategy (Task #4132): the ~1-second update path is the
  // `invalidateResolveQueries` call every in-app resolve/unresolve surface
  // fires (see client/src/pages/app-messages/_resolve-invalidations.ts).
  // The settings below are the self-healing safety net so the badge still
  // corrects itself within seconds even when a resolve happens on a surface
  // that does NOT invalidate (admin impersonation, a future resolve UI) or
  // when the cache was seeded with a stale value before this layout mounted:
  //  - staleTime 0 + refetchOnMount "always": a seeded/cached value is
  //    refetched on mount instead of being trusted forever (the global
  //    queryClient default is staleTime: Infinity).
  //  - refetchOnWindowFocus: catches resolves done in another tab/window.
  //  - refetchInterval 10s: tightened from 30s as the worst-case fallback.
  const messagesUnreadQuery = useQuery<{ count: number }>({
    queryKey: ["/api/app/messages/unread-count"],
    refetchInterval: 10_000,
    refetchOnWindowFocus: true,
    refetchOnMount: "always",
    staleTime: 0,
  });
  const messagesUnreadCount = messagesUnreadQuery.data?.count || 0;


  // Task #1029 — message_drafts table dropped. Surface awaiting-approval
  // messages_pending rows instead so hosts still see a count of items
  // queued for review.
  const messageDraftsQuery = useQuery<{ id: number; autoSend: boolean | null }[]>({
    queryKey: ["/api/app/messages/pending"],
    refetchInterval: 30000,
  });
  const messageDraftCount = (messageDraftsQuery.data ?? []).filter((p) => p.autoSend === false).length;

  // Task #1619 follow-up — Menu Tier Locks: hide nav rows whose href is
  // configured to deny the current user's (or their team owner's) tier.
  // Default behavior: hrefs without a row are visible to everyone.
  const tierLocksQuery = useQuery<{ deniedKeys: string[]; lockedTiers?: Record<string, AccountType> }>({
    queryKey: ["/api/app/menu-tier-locks/denied"],
    staleTime: 60_000,
  });
  const deniedTierLockKeys = new Set<string>(tierLocksQuery.data?.deniedKeys ?? []);
  // Per-key cheapest upgrade tier (cheapest allowed tier strictly above the
  // user's). A denied key WITHOUT an entry here has no reachable upgrade, so
  // the sidebar hides it rather than rendering a locked "Upgrade" row.
  const lockedTiersByKey: Record<string, AccountType> = tierLocksQuery.data?.lockedTiers ?? {};
  // My Daily Pulse is a pinned item (no href→key mapping), so its tier lock is
  // keyed directly. When denied with a reachable upgrade tier it renders
  // locked-visible (Lock + "Upgrade") and opens the upgrade modal on click.
  const PULSE_TIER_LOCK_KEY = "pinned.pulse_ai";
  const pulseLockTier = lockedTiersByKey[PULSE_TIER_LOCK_KEY];
  const pulseLocked = deniedTierLockKeys.has(PULSE_TIER_LOCK_KEY) && !!pulseLockTier;
  // Private services require the specific user's active beta grant.
  const hasTrashBetaAccess = useBetaAccess("trash-service");
  const hasCollectionsBetaAccess = useBetaAccess("collections");
  // Portfolio Analytics is gated behind BOTH the 'portfolio_analytics' beta
  // toggle and the Professional tier. The sidebar row only appears for beta
  // members; within that, sub-Professional plans see a locked upgrade button.
  const hasPortfolioAnalyticsBeta = useBetaAccess("portfolio_analytics");
  // Owner Portal / Owner CRM / PM fee settings are gated behind BOTH the
  // owner_portal beta grant AND the PM Pro tier during the controlled rollout.
  const hasOwnerPortalBeta = useBetaAccess("owner_portal");
  const hasReviewsBeta = useBetaAccess("reviews");
  const betaAccessKeys = useBetaAccessKeys();
  // Task #3976 — admin "Coming Soon" / "Hidden" feature gating. Drives nav
  // visibility (hidden mode only) plus the route overlay further below. Exempt
  // users (admin or granted) receive a `gated:false` entry, so these helpers
  // no-op for them.
  const { byKey: featureGatesByKey, loaded: featureGatesLoaded } = useFeatureGates();
  // B18 — Daily Pulse nav only while the `dailyPulseEnabled` app flag is on
  // (coded default off; the flag read never flashes the item in).
  const { flags: appFlags } = useAppFlags();
  const dailyPulseEnabled = appFlags.dailyPulseEnabled;
  // B19 — sections switched off in shared/hidden-sections.ts (and Daily Pulse
  // while its flag is off) are removed from every nav surface: sidebar groups,
  // favorites and sidebar search.
  function isSwitchedOffHref(href: string): boolean {
    if (isHiddenAppPath(href)) return true;
    if (href === "/app/daily-pulse" && !dailyPulseEnabled) return true;
    return false;
  }
  function tierAllowsHref(href: string): boolean {
    if (isSwitchedOffHref(href)) return false;
    if (href === "/app/operations/trash" && !hasTrashBetaAccess) return false;
    if (href === "/app/financials/collections" && !hasCollectionsBetaAccess) return false;
    if (navHiddenFor(featureGatesByKey, href)) return false;
    const key = MENU_TIER_LOCK_HREF_TO_KEY[href];
    if (!key) return true;
    if (href === "/app/operations/trash" && hasTrashBetaAccess) return true;
    return !deniedTierLockKeys.has(key);
  }

  const userQuery = useQuery<{
    user: { id: number; email: string; name: string | null; approved: boolean; fullName: string | null; selectedPlan?: string; status?: string; profilePictureUrl?: string | null; firstName?: string | null; phone?: string | null; onboardingCompletedAt?: string | null; onboardingWizardCompletedAt?: string | null; founderTier?: string | null; founderStatus?: string | null; founderFreeRooms?: number | null; isAdmin?: boolean; superAdmin?: boolean; adminPermissions?: string[]; tosAcknowledgedAt?: string | null; hasPaymentMethod?: boolean; needsPaymentSetup?: boolean; accountKind?: string | null; trialEndsAt?: string | null; subscriptionStatus?: string | null };
    effectivePlan?: AccountType;
    planInherited?: boolean;
    planInviterUserId?: number | null;
    planInviterName?: string | null;
    activeMembership?: { ownerUserId: number; ownerName: string; ownerEmail: string; role: string } | null;
    isBillableAccountOwner?: boolean;
    inheritedFounderTier?: string | null;
    inheritedFounderStatus?: string | null;
    pendingInvitationCount?: number;
    starterSeedFailedSeeds?: string[] | null;
  }>({
    queryKey: ["/api/app/auth/me"],
    queryFn: async () => {
      const res = await fetch("/api/app/auth/me", { credentials: "include" });
      if (!res.ok) {
        if (res.status === 401) {
          try {
            const body = await res.clone().json();
            if (body && body.code === "SESSION_REVOKED") {
              try {
                sessionStorage.setItem(SESSION_REVOKED_STORAGE_KEY, JSON.stringify({
                  reason: body.reason || "revoked",
                  message: body.message || "Your access has been revoked.",
                  ownerName: body.ownerName ?? null,
                  ownerEmail: body.ownerEmail ?? null,
                  removedAt: body.removedAt ?? null,
                }));
              } catch {}
              // Revoked sessions bypass the identity hook (the page unloads
              // to a route outside AppLayout), so unbind the device here.
              logoutOneSignalIdentity();
              if (!window.location.pathname.startsWith("/app/no-team")) {
                window.location.replace("/app/no-team");
              }
            }
          } catch {}
        }
        // Status-prefixed so isUnauthorizedError() recognizes a 401 — the
        // OneSignal identity hook shares this query key and relies on it to
        // unbind the device when the session dies. Observers on a shared key
        // adopt the last-set queryFn, so this fn's error shape is the one
        // every observer sees.
        throw new Error(`${res.status}: Not authenticated`);
      }
      try { sessionStorage.removeItem(SESSION_REVOKED_STORAGE_KEY); } catch {}
      return res.json();
    },
    retry: false,
  });

  const permsQuery = useQuery<{ isOwner: boolean; merged: AreaPermissions }>({
    queryKey: ["/api/app/my-permissions"],
    enabled: !!userQuery.data?.user,
  });

  const logoutMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => {
      await apiRequest("POST", "/api/app/auth/logout", {});
    },
    onSuccess: () => {
      logoutOneSignalIdentity();
      void resetAppAnalytics();
      clearReturnTo();
      queryClient.clear();
      navigate("/app/login");
    },
  });

  const user = userQuery.data?.user;
  // B10: identify this session in PostHog by account id (after signup/login).
  const analyticsId = analyticsAccountId(userQuery.data as Parameters<typeof analyticsAccountId>[0]);
  useEffect(() => {
    if (analyticsId) void identifyAppAccount(analyticsId);
  }, [analyticsId]);
  // Session replay privacy: mask the whole page unless it's on the readable list.
  useReplayPrivacy(location);
  const mergedPerms = permsQuery.data?.merged;
  const effectivePlan = (userQuery.data?.effectivePlan || user?.selectedPlan || 'Host') as AccountType;
  const planInherited = !!userQuery.data?.planInherited;
  const planInviterName = userQuery.data?.planInviterName || null;
  const activeMembership = userQuery.data?.activeMembership || null;
  const isBillableAccountOwner = userQuery.data?.isBillableAccountOwner ?? !activeMembership;
  // v3 per-property accounts: paused / past-due accounts stay readable (the
  // server blocks writes with 402 ACCOUNT_READ_ONLY) and the v3 banner
  // explains what to do, so the legacy full-page inactive screen never applies.
  const isV3Billing = useIsV3Billing({ enabled: !!user });
  // Why the owner's OWN billing no longer grants access (past_due, trial over
  // without a subscription, subscription ended) — drives the inactive screen
  // and banner. Admins and team members are never shown their own state
  // (team members inherit the owner's billing; a 402 still reaches them via
  // the banner's fallback).
  const ownInactiveReason =
    user && !user.isAdmin && isBillableAccountOwner && user.status !== "waitlist" && !isV3Billing
      ? inactiveSubscriptionReason({
          accountKind: user.accountKind ?? null,
          subscriptionStatus: user.subscriptionStatus ?? null,
          founderStatus: user.founderStatus ?? null,
          trialEndsAt: user.trialEndsAt ?? null,
        })
      : null;
  const inheritedFounderTier = userQuery.data?.inheritedFounderTier || null;
  const inheritedFounderStatus = userQuery.data?.inheritedFounderStatus || null;
  const starterSeedFailedSeeds = userQuery.data?.starterSeedFailedSeeds || null;

  const pendingTasksQuery = useQuery<{ id: number; status: string }[]>({
    queryKey: ["/api/app/tasks"],
    refetchInterval: 60000,
    enabled: !mergedPerms || mergedPerms["maintenance"] !== "disabled",
    select: (data) => data.filter((t) => t.status === "pending" || t.status === "needs_attention"),
  });

  const pendingMailCountQuery = useQuery<{ count: number }>({
    queryKey: ["/api/app/mail-documents/pending-count"],
    refetchInterval: 5 * 60 * 1000,
    enabled: betaAccessKeys.includes("forwarded_docs") && (!mergedPerms || mergedPerms["financials"] !== "disabled"),
  });
  const pendingMailCount = betaAccessKeys.includes("forwarded_docs") ? pendingMailCountQuery.data?.count ?? 0 : 0;

  const [upgradeModal, setUpgradeModal] = useState<{ open: boolean; feature: string; requiredTier: AccountType; reason: string; learnMoreHref?: string }>({ open: false, feature: '', requiredTier: 'Host', reason: '' });
  useEffect(() => {
    if (userQuery.isError) {
      let hasRevokedNotice = false;
      try {
        hasRevokedNotice = !!sessionStorage.getItem(SESSION_REVOKED_STORAGE_KEY);
      } catch {}
      if (!hasRevokedNotice) {
        // Phase 3 — an expired session must not lose the destination: stash
        // the current in-app URL (e.g. a notification tap's conversation deep
        // link, hash included) so the login flow can restore it after
        // re-authentication. Bound to the retained user (React Query keeps
        // the last data on a failed refetch) so a DIFFERENT user logging in
        // on this device can never inherit it.
        stashReturnTo(
          window.location.pathname + window.location.search + window.location.hash,
          userQuery.data?.user?.id ?? null,
        );
      }
      navigate(hasRevokedNotice ? "/app/no-team" : "/app/login");
    }
  }, [userQuery.isError, navigate]);

  useEffect(() => {
    if (!user) return;
    // Task #1706 — Don't run onboarding-stage redirects while the auth/me
    // query is revalidating. After the welcome step's completeMutation
    // invalidates the cache, AppLayout can mount on /app/dashboard and
    // briefly see stale user data (onboardingWizardCompletedAt still null)
    // before the refetch settles, which previously caused a visible flash
    // back to /app/welcome. Waiting for the in-flight refetch eliminates
    // that race.
    if (userQuery.isFetching) return;
    if (!user.approved) {
      navigate("/app/awaiting-approval");
      return;
    }
    if (user.status === 'waitlist') return;
    // Treat any completion signal as "done with onboarding" so we don't
    // bounce a user to /app/onboarding when they've already finished it
    // (e.g. legacy/seeded accounts that completed onboarding before a
    // phone number became required). Without this guard, AppLayout sends
    // them to /app/onboarding while AppOnboardingProfile sends them back
    // to /app/dashboard, causing "Maximum update depth exceeded".
    // Task #1630 — Gate on the wizard-finished flag, not the bot-owned
    // `onboardingCompletedAt`. The bot writes the latter on its own
    // schedule; only the in-app wizard should control this redirect.
    if (user.onboardingWizardCompletedAt) {
      // A user with pending team invitations must resolve them BEFORE the
      // payment gate. A freshly-invited user has no *accepted* membership yet,
      // so `isBillableAccountOwner` is still true and the payment-gate effect
      // below would navigate (and `return`) before the dedicated pending-invite
      // effect could run — pushing the invitee to the card page first
      // (Task #4154). Routing pending invitations here gives that path
      // precedence; after the invitations are resolved the normal gate resumes
      // (declining leaves a billable owner without a card → card page).
      if ((userQuery.data?.pendingInvitationCount ?? 0) > 0) {
        navigate("/app/accept-invitations");
        return;
      }
      // Only gate users who signed up after the T&C / card-on-file feature
      // was introduced (tosAcknowledgedAt is set). Legacy users that
      // completed onboarding before this change have null tosAcknowledgedAt
      // and must not be forced through the payment step.
      // Additionally, only the billable account owner is ever charged for a
      // subscription — team members (plan inherited from the owner) and
      // resident members must never be sent to the card-on-file page.
      // Modular owners need a LIVE SUBSCRIPTION, not just a saved card:
      // elapsed trial time never creates one, only completing Checkout does
      // (`needsPaymentSetup`, shared/subscription-gate.ts). An owner whose
      // trial or subscription has already lapsed is NOT bounced — they keep
      // the free P&L, and gated pages render the inactive screen (below),
      // which leads to Checkout.
      if (
        !user.isAdmin &&
        isBillableAccountOwner &&
        user.tosAcknowledgedAt &&
        needsPaymentSetupFromResponse(user) &&
        !ownInactiveReason
      ) {
        navigate("/app/onboarding/payment");
        return;
      }
      return;
    }
    if (!user.phone) {
      navigate("/app/onboarding");
      return;
    }
    navigate("/app/welcome");
  }, [user, navigate, userQuery.isFetching, ownInactiveReason]);

  useEffect(() => {
    if (!userQuery.data || !user || !user.approved) return;
    if (location.startsWith("/app/no-team") || location.startsWith("/app/awaiting-approval") || location.startsWith("/app/login") || location.startsWith("/app/profile")) return;
    const ownPlan = (user.selectedPlan || 'Host') as AccountType;
    const ownPlanIsInterest = ownPlan === 'Interest' || user.status === 'waitlist';
    if (ownPlanIsInterest && !activeMembership) {
      navigate("/app/no-team");
    }
  }, [userQuery.data, user, activeMembership, location, navigate]);

  useEffect(() => {
    if (!userQuery.data || !user || !user.approved) return;
    if (location.startsWith("/app/accept-invitations") || location.startsWith("/app/no-team") || location.startsWith("/app/awaiting-approval") || location.startsWith("/app/login")) return;
    const pending = userQuery.data.pendingInvitationCount ?? 0;
    if (pending > 0 && user.phone && user.onboardingWizardCompletedAt) {
      navigate("/app/accept-invitations");
    }
  }, [userQuery.data, user, location, navigate]);

  if (userQuery.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Skeleton className="h-8 w-32" />
      </div>
    );
  }

  if (userQuery.isError) {
    return null;
  }

  if (user && !user.approved) {
    return null;
  }

  if (user && user.approved && user.status !== 'waitlist' && !user.onboardingWizardCompletedAt) {
    return null;
  }

  const visibleNavItems = navItems
    .filter((item) => tierAllowsHref(item.href))
    .map((item) => ({
      ...item,
      isDisabled: item.area && mergedPerms ? mergedPerms[item.area] === "disabled" : false,
    }));

  const showProperties = !mergedPerms || mergedPerms["properties"] !== "disabled";
  const showUnderwriting = !mergedPerms || mergedPerms["underwriting"] !== "disabled";
  const showPricing = !mergedPerms || mergedPerms["pricing"] !== "disabled";
  const showDevices = !mergedPerms || mergedPerms["devices"] !== "disabled";
  const showOperations = !mergedPerms || mergedPerms["maintenance"] !== "disabled";

  const pendingTaskCount = pendingTasksQuery.data?.length || 0;
  const showFinancials = !mergedPerms || mergedPerms["financials"] !== "disabled";
  const showTeam = !mergedPerms || mergedPerms["team"] !== "disabled";
  const showTeamPage = showTeam || !!activeMembership;
  // Admin team members manage the owner's billing, so they get the Billing
  // link even when their team-area permission is disabled.
  const showBilling = showTeam || activeMembership?.role === "admin";
  const showPadsplitSync = !mergedPerms || mergedPerms["padsplit_sync"] !== "disabled";

  const userPlan = effectivePlan;
  // Modular accounts have no legacy tier — access is per-entitlement (server
  // enforced; sidebar emblems come from /menu-tier-locks/denied, which is
  // entitlement-derived for them). Legacy tier locks must never fire.
  const isModularAccount = (user as { accountKind?: string | null } | null | undefined)?.accountKind != null;

  function isTierLocked(requiredTier: AccountType): boolean {
    if (isModularAccount) return false;
    return !hasFeatureAccess(userPlan, requiredTier);
  }

  function openUpgrade(feature: string, requiredTier: AccountType, reason: string, learnMoreHref?: string) {
    setUpgradeModal({ open: true, feature, requiredTier, reason, learnMoreHref });
  }

  // Decide how a tier-lockable sub-item should render. Items in the
  // locked-visible groups (Members / Operations / Revenue) stay in the sidebar
  // as a locked "Upgrade" row when denied (so users discover gated features),
  // unless there is no reachable upgrade tier, in which case they are hidden.
  function subItemLockState(href: string):
    | { kind: "link" }
    | { kind: "locked"; tier: AccountType; learnMoreHref?: string }
    | { kind: "hidden" } {
    if (isSwitchedOffHref(href)) return { kind: "hidden" };
    if (navHiddenFor(featureGatesByKey, href)) return { kind: "hidden" };
    const key = MENU_TIER_LOCK_HREF_TO_KEY[href];
    if (!key || !deniedTierLockKeys.has(key)) return { kind: "link" };
    if (href === "/app/operations/trash" && hasTrashBetaAccess) return { kind: "link" };
    const tier = lockedTiersByKey[key];
    if (!tier) return { kind: "hidden" };
    return { kind: "locked", tier, learnMoreHref: FEATURE_LEARN_MORE_HREF[href] };
  }

  // Resolve the current route to its tier-lock state for the route-level
  // overlay. Longest-prefix match over the registry so unregistered Financials
  // subroutes (/mail-inbox, /statements, /cards) inherit the /app/financials
  // lock — closing the gap where those paths bypassed the sidebar gate.
  // Returns null unless the matched key is denied for the user's tier (with a
  // reachable upgrade tier), so non-locked routes render normally.
  function routeTierLock(path: string):
    | { key: string; href: string; label: string; tier: AccountType; learnMoreHref?: string }
    | null {
    // Path → section (key + href) via the shared resolver, so the route overlay
    // and the global modal's section match (`routeMenuLockKey`) can't drift.
    const match = resolveRouteMenuLock(path);
    if (match === null) return null;
    const { key, href: bestHref } = match;
    if (!deniedTierLockKeys.has(key)) return null;
    const tier = lockedTiersByKey[key];
    if (!tier) return null;
    let p = path.split("?")[0].split("#")[0];
    if (p.length > 1 && p.endsWith("/")) p = p.slice(0, -1);
    const label =
      SIDEBAR_ITEM_REGISTRY[p]?.label ?? SIDEBAR_ITEM_REGISTRY[bestHref]?.label ?? bestHref;
    return { key, href: bestHref, label, tier, learnMoreHref: FEATURE_LEARN_MORE_HREF[bestHref] };
  }

  function isHrefAccessible(href: string): boolean {
    if (isSwitchedOffHref(href)) return false;
    const privateBeta = privateBetaForPath(href);
    if (privateBeta && !betaAccessKeys.includes(privateBeta.key)) return false;
    if (href === "/app/analytics" && (!hasPortfolioAnalyticsBeta || isTierLocked("Professional"))) return false;
    if (href === "/app/roadmap" || href === "/app/testimonials") return false;
    if (href === "/app/devices/cameras") return false;
    if (href === "/app/operations/trash" && !hasTrashBetaAccess) return false;
    if (href === "/app/financials/collections" && !hasCollectionsBetaAccess) return false;
    if (!SIDEBAR_ITEM_REGISTRY[href]) return false;
    if (navHiddenFor(featureGatesByKey, href)) return false;
    if (href === "/app/dashboard") return true;
    if (href === "/app/properties") return showProperties;
    if (href === "/app/messages") return !mergedPerms || mergedPerms["messages"] !== "disabled";
    if (href === "/app/members" || href === "/app/members/vehicles" || href === "/app/members/booking-requests" || href === "/app/members/booking-requests?tab=templates" || href === "/app/members/booking-requests?tab=submissions" || href === "/app/members/booking-requests/settings") {
      return !mergedPerms || mergedPerms["members"] !== "disabled";
    }
    if (href === "/app/research" || href === "/app/buy-box/settings" || href === "/app/buy-box/agents" || href === "/app/buy-box/reports" || href === "/app/buy-box/saved" || href === "/app/acquisitions" || href === "/app/find-realtor" || href === "/app/finance" || href === "/app/buy" || href === "/app/renovations" || href === "/app/furnishing-catalog" || href === "/app/property-launch") {
      return showProperties;
    }
    if (href === "/app/connections") return tierAllowsHref(href);
    if (href === "/app/underwriting") return showProperties && showUnderwriting;
    if (href === "/app/sell") return showProperties;
    if (href === "/app/pricing" || href === "/app/prompt-library") return showPricing;
    if (href === "/app/marketing/direct-bookings" || href === "/app/marketing/third-party-listings") {
      return showPricing && !isTierLocked("Professional");
    }
    if (href === "/app/reviews") return showOperations && hasReviewsBeta;
    if (href === "/app/cleaning") return showOperations;
    if (href === "/app/tasks" || href === "/app/operations/task-rules" || href === "/app/operations/trash" || href === "/app/operations/service-forms" || href === "/app/account/knowledge-base") return showOperations;
    if (href === "/app/supplies") return showOperations && !isTierLocked("Professional");
    if (href === "/app/operations/vendors" || href === "/app/operations/approvals" || href === "/app/operations/marketplace" || href === "/app/operations/vendors/settings") {
      return showOperations;
    }
    if (href === "/app/operations/owners" || href === "/app/operations/owner-invoices" || href === "/app/account/pm-billing") {
      return hasOwnerPortalBeta && !isTierLocked("PMPro");
    }
    if (href.startsWith("/app/financials")) return showFinancials;
    if (href.startsWith("/app/devices")) return showDevices && !isTierLocked("Professional");
    if (href === "/app/team") return showTeamPage;
    if (href === "/app/account/billing") return showBilling;
    if (href === "/app/padsplit-sync") return showPadsplitSync;
    return true;
  }

  const accessibleFavorites = favorites.filter(isHrefAccessible);
  const searchableNavigation = Object.entries(SIDEBAR_ITEM_REGISTRY)
    .filter(([href]) => isHrefAccessible(href) && subItemLockState(href).kind === "link")
    .map(([href, item]) => ({ href, ...item }));

  function isFavoriteActive(href: string): boolean {
    if (href === "/app/dashboard") return location === "/app/dashboard";
    if (href.includes("?")) {
      const [base, qs] = href.split("?");
      const params = new URLSearchParams(qs);
      return location.startsWith(base) && params.toString().split("&").every((kv) => {
        const [k, v] = kv.split("=");
        return location.includes(`${k}=${v}`);
      });
    }
    return location.startsWith(href);
  }

  const propertyNavigationItems: SidebarNavItem[] = [
    { label: "Research", href: "/app/research", icon: TrendingUp, gate: () => true, testid: "link-sidebar-research" },
    { label: "Underwrite", href: "/app/underwriting", icon: ClipboardList, gate: () => showUnderwriting, testid: "link-sidebar-underwriting" },
  ];

  // Buy Box remains an independent collapsible, not coupled to Research.
  const buyBoxSubItems: SidebarNavItem[] = [
    { label: "Reports", href: "/app/buy-box/reports", icon: FileText, gate: () => true, testid: "link-sidebar-buy-box-reports" },
    { label: "Saved", href: "/app/buy-box/saved", icon: Star, gate: () => true, testid: "link-sidebar-buy-box-saved" },
    { label: "Agents", href: "/app/buy-box/agents", icon: Bot, gate: () => true, testid: "link-sidebar-buy-box-agents" },
    { label: "Settings", href: "/app/buy-box/settings", icon: Settings2, gate: () => true, testid: "link-sidebar-buy-box-settings" },
  ];
  const visibleBuyBoxSubItems = buyBoxSubItems.filter(
    (item) => item.gate() && isHrefAccessible(item.href) && tierAllowsHref(item.href),
  );

  const resourceItems: SidebarNavItem[] = [
    { label: "Earn $250", href: "/app/resources/mercury", icon: Landmark, gate: () => true, testid: "link-sidebar-earn-250", highlighted: true },
    { label: "Connections", href: "/app/connections", icon: Handshake, gate: () => true, testid: "link-sidebar-connections" },
    { label: "Find a Realtor", href: "/app/find-realtor", icon: UserCheck, gate: () => true, testid: "link-sidebar-find-realtor" },
    { label: "Get Financing", href: "/app/finance", icon: Landmark, gate: () => true, testid: "link-sidebar-finance" },
  ];
  const visibleResourceItems = resourceItems.filter(
    (item) => item.gate() && isHrefAccessible(item.href) && tierAllowsHref(item.href),
  );

  // Nested "Marketing" collapsible inside Operations (rendered after
  // Pricing / Rooms).
  const marketingSubItems: SidebarNavItem[] = [
    { label: "Direct Bookings", href: "/app/marketing/direct-bookings", icon: Globe, gate: () => true, testid: "link-sidebar-direct-bookings" },
    { label: "Third Party Listings", href: "/app/marketing/third-party-listings", icon: ListChecks, gate: () => true, testid: "link-sidebar-third-party-listings" },
  ];

  const showMembers = !mergedPerms || mergedPerms["members"] !== "disabled";

  const membersSubItems = [
    { label: "Members", href: "/app/members", icon: UserCheck, testid: "link-sidebar-members-list", gate: () => showMembers },
    { label: "Vehicles", href: "/app/members/vehicles", icon: Car, testid: "link-sidebar-vehicles", gate: () => showMembers, indent: true },
    { label: "Reviews", href: "/app/reviews", icon: Star, testid: "link-sidebar-reviews", gate: () => showOperations && hasReviewsBeta, indent: true },
  ];

  const operationsSubItems: SidebarNavItem[] = [
    { label: "Tasks", href: "/app/tasks", icon: ClipboardCheck, gate: () => showOperations },
    // Forms-on-tasks Phase 4 — Task Rules is the unified recurrence engine
    // (it absorbed the service-form schedules UI), so the beta gate is lifted.
    // Nested directly under Tasks (it automates them).
    { label: "Task Rules", href: "/app/operations/task-rules", icon: Zap, gate: () => showOperations, indent: true },
    { label: "Forms", href: "/app/operations/service-forms", icon: ClipboardList, gate: () => showOperations, indent: true },
    // Member Cleaning System (Build 2) — beta-gated while it rolls out.
    { label: "Cleaning Program", href: "/app/cleaning", icon: Sparkles, gate: () => showOperations },
    { label: "To-Dos", href: "/app/operations/todos", icon: ListTodo, gate: () => showOperations, testid: "link-sidebar-todos" },
    { label: "Supplies", href: "/app/supplies", icon: Package, gate: () => showOperations },
    { label: "Trash Service", href: "/app/operations/trash", icon: Trash2, gate: () => showOperations && hasTrashBetaAccess, testid: "link-sidebar-trash-service" },
    { label: "Pricing / Rooms", href: "/app/pricing", icon: DollarSign, gate: () => showPricing, testid: "link-sidebar-rooms-overview" },
    { label: "Collections", href: "/app/financials/collections", icon: AlertCircle, gate: () => showFinancials && hasCollectionsBetaAccess, testid: "link-sidebar-collections" },
  ];

  const devicesSubItems = [
    { label: "Overview", href: "/app/devices", icon: Wifi },
    { label: "Smart Locks", href: "/app/devices/smart-locks", icon: Lock },
    { label: "Thermostats", href: "/app/devices/thermostats", icon: Thermometer },
  ];

  const ownersSubItems = [
    { label: "Owners Directory", href: "/app/operations/owners", icon: Handshake, testid: "link-sidebar-owners" },
    { label: "Owner Invoices", href: "/app/operations/owner-invoices", icon: FileText, testid: "link-sidebar-owner-invoices" },
    { label: "Owner Settings", href: "/app/account/pm-billing", icon: DollarSign, testid: "link-sidebar-owner-settings" },
  ];

  const vendorSubItems = [
    { label: "Vendor Directory", href: "/app/operations/vendors", icon: Users },
    { label: "Vendor Marketplace", href: "/app/operations/marketplace", icon: Users },
    { label: "Vendor Payments", href: "/app/operations/approvals", icon: CreditCard },
    { label: "Vendor Settings", href: "/app/operations/vendors/settings", icon: Settings },
  ];


  return (
    <div className="h-[100dvh] min-h-0 flex overflow-hidden bg-white" data-testid="app-shell">
      {isMobile && sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-30" onClick={() => setSidebarOpen(false)} />
      )}
      <aside
        className={`${isMobile ? "fixed inset-y-0 left-0 z-40" : "relative"} h-[100dvh] min-h-0 shrink-0 border-r border-gray-200 flex flex-col bg-white transition-all duration-300 overflow-hidden ${
          !sidebarOpen && isMobile ? "-translate-x-full invisible" : ""
        }`}
        style={{
          width: isMobile ? DEFAULT_SIDEBAR_WIDTH : sidebarOpen ? sidebarWidth.width : 48,
          ...(sidebarWidth.isResizing ? { transition: "none" } : {}),
        }}
        id="app-sidebar"
        data-testid="app-sidebar"
      >
        {!isMobile && sidebarOpen && (
          <div
            {...sidebarWidth.handleProps}
            data-testid="sidebar-resize-handle"
            className={`absolute inset-y-0 right-0 z-50 w-2 cursor-col-resize touch-none select-none hover:bg-brand/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand ${sidebarWidth.isResizing ? "bg-brand/20" : ""}`}
          >
            <span aria-hidden="true" className="pointer-events-none absolute right-0.5 top-1/2 h-8 w-0.5 -translate-y-1/2 rounded-full bg-gray-300 dark:bg-gray-600" />
          </div>
        )}
        <div className={`shrink-0 border-b border-gray-200 flex items-center ${sidebarOpen ? "p-4 justify-between" : "p-2 justify-center"}`}>
          {sidebarOpen ? (
            <>
              <Link href="/app/dashboard">
                <img src={logoImg} alt="PadPulse" className="h-[40px] w-auto cursor-pointer" data-testid="link-sidebar-logo" />
              </Link>
              <button onClick={() => { setSidebarOpen(false); setFavoritesExpanded(false); setBuyBoxExpanded(false); setOperationsExpanded(false); setMarketingExpanded(false); setDevicesExpanded(false); setVendorsExpanded(false); setOwnersExpanded(false); }} className="p-1 rounded hover:bg-gray-100 text-gray-500" data-testid="button-collapse-sidebar">
                <ChevronLeft className="h-4 w-4" />
              </button>
            </>
          ) : (
            <button onClick={() => {
              setSidebarOpen(true);
              if (isBuyBoxRoute) setBuyBoxExpanded(true);
              if (isResourcesRoute) setResourcesExpanded(true);
              if (isOperationsGroupRoute) setOperationsExpanded(true);
              if (isMarketingRoute) setMarketingExpanded(true);
              if (isDevicesGroupRoute) setDevicesExpanded(true);
              if (isVendorRoute) setVendorsExpanded(true);
              if (isOwnersGroupRoute) setOwnersExpanded(true);
            }} className="p-1 rounded hover:bg-gray-100 text-gray-500" data-testid="button-expand-sidebar">
              <ChevronRight className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="shrink-0 border-b border-gray-200 px-1.5 py-2 space-y-1" data-testid="sidebar-quick-actions">
          <SidebarSearch items={searchableNavigation} sidebarOpen={sidebarOpen} onNavigate={() => { if (isMobile) setSidebarOpen(false); }} />
          {user && <GetStartedLauncher sidebarOpen={sidebarOpen} isMobile={isMobile} />}
        </div>
        <nav className="sidebar-scrollbar flex-1 min-h-0 py-2 flex flex-col overflow-y-auto overscroll-contain whitespace-nowrap" data-testid="sidebar-navigation">
          <TooltipProvider delayDuration={300}>

          {/* ─── PINNED: Favorites ─── */}
          {tierAllowsHref('/app/favorites') && (!sidebarOpen && !isMobile ? (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  className="w-full flex items-center justify-center py-2.5 text-sm cursor-pointer text-amber-500 hover:bg-amber-50"
                  title="Favorites"
                  data-testid="button-sidebar-favorites-collapsed"
                >
                  <Star className={`h-4 w-4 shrink-0 ${accessibleFavorites.length > 0 ? "fill-amber-400 text-amber-400" : ""}`} />
                </button>
              </PopoverTrigger>
              <PopoverContent side="right" align="start" className="w-52 p-1">
                <p className="px-2 py-1.5 text-xs font-semibold text-gray-500 flex items-center gap-1.5">
                  <Star className="h-3 w-3 fill-amber-400 text-amber-400" /> Favorites
                </p>
                {accessibleFavorites.length === 0 ? (
                  <p className="px-2 py-2 text-xs text-gray-400 leading-relaxed">Click the ★ next to any menu item to pin it here.</p>
                ) : (
                  accessibleFavorites.map((href) => {
                    const meta = SIDEBAR_ITEM_REGISTRY[href];
                    if (!meta) return null;
                    const ItemIcon = meta.icon;
                    const isActive = isFavoriteActive(href);
                    return (
                      <div key={href} className="flex items-center gap-1 pr-1">
                        <Link href={href} className="flex-1 min-w-0">
                          <span className={`flex items-center gap-2 px-2 py-1.5 text-sm rounded cursor-pointer ${isActive ? "text-gray-900 font-medium bg-gray-50" : "text-gray-600 hover:bg-gray-50"}`} data-testid={`link-favorite-collapsed${href.replace(/\//g, "-")}`}>
                            <ItemIcon className="h-3.5 w-3.5 shrink-0" />
                            <span className="truncate">{meta.label}</span>
                          </span>
                        </Link>
                        <button
                          className="shrink-0 p-1 rounded hover:bg-gray-100"
                          onClick={() => toggleFavorite(href)}
                          title="Remove from favorites"
                          data-testid={`btn-unfavorite-collapsed${href.replace(/\//g, "-")}`}
                        >
                          <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                        </button>
                      </div>
                    );
                  })
                )}
              </PopoverContent>
            </Popover>
          ) : (
            <>
              <button
                onClick={() => setFavoritesExpanded((v) => !v)}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm cursor-pointer text-amber-600 hover:bg-amber-50"
                data-testid="button-sidebar-favorites"
              >
                <Star className={`h-4 w-4 shrink-0 ${accessibleFavorites.length > 0 ? "fill-amber-400 text-amber-400" : ""}`} />
                <span className="flex-1 text-left font-medium">Favorites</span>
                {favoritesExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              </button>
              {favoritesExpanded && (
                <>
                  {accessibleFavorites.length === 0 ? (
                    <p className="pl-9 pr-4 py-2 text-xs text-gray-400 leading-relaxed">Click the ★ next to any menu item to pin it here.</p>
                  ) : (
                    <DndContext
                      sensors={favoritesDragSensors}
                      collisionDetection={closestCenter}
                      onDragStart={handleFavoritesDragStart}
                      onDragEnd={(e) => handleFavoritesDragEnd(e, accessibleFavorites)}
                      onDragCancel={() => setActiveFavoriteId(null)}
                    >
                      <SortableContext items={accessibleFavorites} strategy={verticalListSortingStrategy}>
                        {accessibleFavorites.map((href) => {
                          const meta = SIDEBAR_ITEM_REGISTRY[href];
                          if (!meta) return null;
                          return (
                            <SortableFavoriteItem
                              key={href}
                              href={href}
                              label={meta.label}
                              Icon={meta.icon}
                              isActive={isFavoriteActive(href)}
                              onUnfavorite={toggleFavorite}
                            />
                          );
                        })}
                      </SortableContext>
                      <DragOverlay dropAnimation={null}>
                        {activeFavoriteId && SIDEBAR_ITEM_REGISTRY[activeFavoriteId] ? (
                          <div
                            className="flex items-center gap-2 px-3 py-2 text-sm rounded-md bg-white shadow-lg ring-1 ring-black/10 text-gray-900 cursor-grabbing"
                            data-testid={`drag-overlay-favorite${activeFavoriteId.replace(/\//g, "-")}`}
                          >
                            {(() => {
                              const Icon = SIDEBAR_ITEM_REGISTRY[activeFavoriteId].icon;
                              return <Icon className="h-4 w-4 shrink-0" />;
                            })()}
                            <span className="truncate max-w-[180px]" title={SIDEBAR_ITEM_REGISTRY[activeFavoriteId].label}>{SIDEBAR_ITEM_REGISTRY[activeFavoriteId].label}</span>
                          </div>
                        ) : null}
                      </DragOverlay>
                    </DndContext>
                  )}
                </>
              )}
            </>
          ))}


          {/* ─── PINNED: My Daily Pulse ─── */}
          {/* B18 — shown only while the `dailyPulseEnabled` app flag is on.
              Intentionally not gated by the Pulse tier lock — every tier should
              see this entry. Accounts without access land on a sample preview
              with an upgrade CTA rather than the live briefing. */}
          {dailyPulseEnabled && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                role="link"
                tabIndex={0}
                onClick={() => pulseLocked ? openUpgrade("My Daily Pulse", pulseLockTier as AccountType, `My Daily Pulse requires the ${planDisplayName(pulseLockTier)} plan.`, FEATURE_LEARN_MORE_HREF["/app/daily-pulse"]) : navigate("/app/daily-pulse")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    if (pulseLocked) {
                      openUpgrade("My Daily Pulse", pulseLockTier as AccountType, `My Daily Pulse requires the ${planDisplayName(pulseLockTier)} plan.`, FEATURE_LEARN_MORE_HREF["/app/daily-pulse"]);
                    } else {
                      navigate("/app/daily-pulse");
                    }
                  }
                }}
                className={`group relative flex items-center gap-3 py-2.5 text-sm cursor-pointer ${sidebarOpen ? "px-4" : "px-0 justify-center"} ${
                  location === "/app/daily-pulse" ? "text-gray-900 font-medium bg-gray-50" : "text-gray-600 hover:bg-gray-50"
                }`}
                data-testid={pulseLocked ? "link-sidebar-daily-pulse-locked" : "link-sidebar-daily-pulse"}
              >
                <Activity className="h-4 w-4 shrink-0" />
                {sidebarOpen && <span className="flex-1">My Daily Pulse</span>}
                {sidebarOpen && pulseLocked && (
                  <>
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-500">Upgrade</span>
                    <Lock className="h-3 w-3 shrink-0 text-amber-500" />
                  </>
                )}
                {sidebarOpen && !pulseLocked && <FavoriteStar href="/app/daily-pulse" favorites={favorites} onToggle={toggleFavorite} />}
              </div>
            </TooltipTrigger>
            {!sidebarOpen && !isMobile && (
              <TooltipContent side="right">My Daily Pulse</TooltipContent>
            )}
          </Tooltip>
          )}

          {/* ─── PINNED: Dashboard ─── */}
          {tierAllowsHref('/app/dashboard') && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Link
                href="/app/dashboard"
                className={`group relative flex items-center gap-3 py-2.5 text-sm cursor-pointer ${sidebarOpen ? "px-4" : "px-0 justify-center"} ${
                  location === "/app" || location === "/app/dashboard" ? "text-gray-900 font-medium bg-gray-50" : "text-gray-600 hover:bg-gray-50"
                }`}
                data-testid="link-sidebar-dashboard"
              >
                <Home className="h-4 w-4 shrink-0" />
                {sidebarOpen && <span className="flex-1">Home</span>}
                {sidebarOpen && messageDraftCount > 0 && (
                    <button
                      type="button"
                      className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-semibold rounded-full bg-blue-600 text-white px-1.5 hover:bg-blue-700 cursor-pointer"
                      title={`${messageDraftCount} draft message${messageDraftCount !== 1 ? "s" : ""}`}
                      data-testid="badge-sidebar-dashboard-drafts"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        navigate("/app/messages?tab=awaiting");
                      }}
                    >
                      {messageDraftCount > 99 ? "99+" : messageDraftCount}
                    </button>
                  )}
                  {!sidebarOpen && messageDraftCount > 0 && (
                    <button
                      type="button"
                      className="absolute top-1 right-1 inline-flex items-center justify-center min-w-[14px] h-[14px] text-[9px] font-semibold rounded-full bg-blue-600 text-white px-1 hover:bg-blue-700 cursor-pointer"
                      title={`${messageDraftCount} draft message${messageDraftCount !== 1 ? "s" : ""}`}
                      data-testid="badge-sidebar-dashboard-drafts-collapsed"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        navigate("/app/messages?tab=awaiting");
                      }}
                    >
                      {messageDraftCount > 99 ? "99+" : messageDraftCount}
                    </button>
                  )}
                {sidebarOpen && <FavoriteStar href="/app/dashboard" favorites={favorites} onToggle={toggleFavorite} />}
              </Link>
            </TooltipTrigger>
            {!sidebarOpen && !isMobile && (
              <TooltipContent side="right">
                Home{messageDraftCount > 0 ? ` (${messageDraftCount} draft${messageDraftCount !== 1 ? "s" : ""})` : ""}
              </TooltipContent>
            )}
          </Tooltip>
          )}

          {/* ─── PINNED: Booking Requests, then Messages ─── */}
          {visibleNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.startsWith(item.href);
            if (item.isDisabled) {
              return (
                <Tooltip key={item.label}>
                  <TooltipTrigger asChild>
                    <span
                      className={`flex items-center gap-3 py-2.5 text-sm cursor-not-allowed opacity-40 ${sidebarOpen ? "px-4" : "px-0 justify-center"}`}
                      data-testid={`link-sidebar-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      {sidebarOpen && item.label}
                    </span>
                  </TooltipTrigger>
                  {!sidebarOpen && !isMobile && (
                    <TooltipContent side="right">{item.label} (Disabled)</TooltipContent>
                  )}
                  {sidebarOpen && (
                    <TooltipContent side="right">Access disabled</TooltipContent>
                  )}
                </Tooltip>
              );
            }
            return (
              <Tooltip key={item.label}>
                <TooltipTrigger asChild>
                  <Link href={item.href}>
                    <span
                      className={`group relative flex items-center gap-3 py-2.5 text-sm cursor-pointer ${sidebarOpen ? "px-4" : "px-0 justify-center"} ${
                        isActive ? "text-gray-900 font-medium bg-gray-50" : "text-gray-600 hover:bg-gray-50"
                      }`}
                      data-testid={`link-sidebar-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      {sidebarOpen && <span className="flex-1">{item.label}</span>}
                      {item.href === "/app/members/booking-requests" && bookingUnreadCount > 0 && (
                        <span
                          className={`inline-flex items-center justify-center text-[10px] font-semibold rounded-full bg-blue-600 text-white px-1.5 ${sidebarOpen ? "min-w-[20px] h-5" : "absolute top-1 right-1 min-w-[14px] h-[14px]"}`}
                          data-testid={sidebarOpen ? "badge-booking-unread" : "badge-booking-unread-collapsed"}
                        >
                          {bookingUnreadCount > 99 ? "99+" : bookingUnreadCount}
                        </span>
                      )}
                      {sidebarOpen && item.href === "/app/messages" && messagesUnreadCount > 0 && (
                        <span className="inline-flex items-center justify-center min-w-[20px] h-5 text-[10px] font-semibold rounded-full bg-blue-600 text-white px-1.5" data-testid="badge-messages-unread">
                          {messagesUnreadCount > 99 ? "99+" : messagesUnreadCount}
                        </span>
                      )}
                      {sidebarOpen && <FavoriteStar href={item.href} favorites={favorites} onToggle={toggleFavorite} />}
                    </span>
                  </Link>
                </TooltipTrigger>
                {!sidebarOpen && !isMobile && (
                  <TooltipContent side="right">{item.label}</TooltipContent>
                )}
              </Tooltip>
            );
          })}

          {/* ─── PINNED: Properties ─── */}
          {showProperties && tierAllowsHref('/app/properties') && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Link href="/app/properties">
                  <span
                    className={`group flex items-center gap-3 py-2.5 text-sm cursor-pointer ${sidebarOpen ? "px-4" : "px-0 justify-center"} ${
                      location.startsWith("/app/properties") ? "text-gray-900 font-medium bg-gray-50" : "text-gray-600 hover:bg-gray-50"
                    }`}
                    data-testid="link-sidebar-my-properties"
                  >
                    <LayoutList className="h-4 w-4 shrink-0" />
                    {sidebarOpen && <span className="flex-1">Properties</span>}
                    {sidebarOpen && <FavoriteStar href="/app/properties" favorites={favorites} onToggle={toggleFavorite} />}
                  </span>
                </Link>
              </TooltipTrigger>
              {!sidebarOpen && !isMobile && (
                <TooltipContent side="right">Properties</TooltipContent>
              )}
            </Tooltip>
          )}

          {/* ─── PROPERTIES LINKS ─── */}
          {showProperties && propertyNavigationItems.map((item) => {
            if (!item.gate() || !isHrefAccessible(item.href) || !tierAllowsHref(item.href)) return null;
            const Icon = item.icon;
            const active = location.startsWith(item.href);
            if (!sidebarOpen && !isMobile) {
              return (
                <Tooltip key={item.href}>
                  <TooltipTrigger asChild>
                    <Link href={item.href}>
                      <span className={`relative flex justify-center py-2.5 ${active ? "text-gray-900 bg-gray-50" : "text-gray-600 hover:bg-gray-50"}`} data-testid={item.testid}>
                        <Icon className="h-4 w-4" />
                      </span>
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right">{item.label}</TooltipContent>
                </Tooltip>
              );
            }
            return (
              <Link key={item.href} href={item.href}>
                <span className={`group flex items-center gap-3 pl-9 pr-2 py-2 text-sm cursor-pointer ${active ? "text-gray-900 font-medium bg-gray-50" : "text-gray-600 hover:bg-gray-50"}`} data-testid={item.testid}>
                  <Icon className="h-4 w-4" />
                  <span className="flex-1">{item.label}</span>
                  <FavoriteStar href={item.href} favorites={favorites} onToggle={toggleFavorite} />
                </span>
              </Link>
            );
          })}
          {showProperties && visibleBuyBoxSubItems.length > 0 && (
            !sidebarOpen && !isMobile ? (
              <Popover>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <PopoverTrigger asChild>
                      <button type="button" className={`flex w-full justify-center py-2.5 ${isBuyBoxRoute ? "text-gray-900 bg-gray-50" : "text-gray-600 hover:bg-gray-50"}`} data-testid="button-sidebar-buy-box" aria-label="Buy Box">
                        <Target className="h-4 w-4" />
                      </button>
                    </PopoverTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="right">Buy Box</TooltipContent>
                </Tooltip>
                <PopoverContent side="right" align="start" className="w-48 p-1">
                  <button type="button" onClick={() => setBuyBoxExpanded((value) => !value)} className="flex w-full items-center gap-2 px-2 py-1.5 text-sm font-medium" aria-expanded={buyBoxExpanded}>
                    <Target className="h-3.5 w-3.5" /><span className="flex-1 text-left">Buy Box</span>
                    {buyBoxExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                  </button>
                  {buyBoxExpanded && visibleBuyBoxSubItems.map((item) => {
                    const Icon = item.icon;
                    return <Link key={item.href} href={item.href}><span className={`flex items-center gap-2 pl-6 pr-2 py-1.5 text-sm rounded ${location.startsWith(item.href) ? "text-gray-900 bg-gray-50" : "text-gray-600 hover:bg-gray-50"}`} data-testid={item.testid}><Icon className="h-3.5 w-3.5" />{item.label}</span></Link>;
                  })}
                </PopoverContent>
              </Popover>
            ) : (
              <>
                <button type="button" onClick={() => setBuyBoxExpanded((value) => !value)} className={`w-full flex items-center gap-3 pl-9 pr-2 py-2 text-sm cursor-pointer ${isBuyBoxRoute ? "text-gray-900 font-medium bg-gray-50" : "text-gray-600 hover:bg-gray-50"}`} data-testid="button-sidebar-buy-box" aria-expanded={buyBoxExpanded}>
                  <Target className="h-4 w-4" />
                  <span className="flex-1 text-left">Buy Box</span>
                  {buyBoxExpanded ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                </button>
                {buyBoxExpanded && visibleBuyBoxSubItems.map((item) => {
                  const Icon = item.icon;
                  const active = location.startsWith(item.href);
                  return <Link key={item.href} href={item.href}><span className={`group flex items-center gap-3 pl-14 pr-2 py-2 text-sm cursor-pointer ${active ? "text-gray-900 font-medium bg-gray-50" : "text-gray-600 hover:bg-gray-50"}`} data-testid={item.testid}><Icon className="h-4 w-4" /><span className="flex-1">{item.label}</span><FavoriteStar href={item.href} favorites={favorites} onToggle={toggleFavorite} /></span></Link>;
                })}
              </>
            )
          )}

          {/* ─── MEMBERS: always-visible links, no dropdown ─── */}
          {membersSubItems.filter(sub => sub.gate()).map(sub => {
            const lockState = subItemLockState(sub.href);
            if (lockState.kind === "hidden") return null;
            const SubIcon = sub.icon;
            const locked = lockState.kind === "locked";
            const active = sub.href === "/app/members"
              ? location === "/app/members" || (location.startsWith("/app/members/") && !location.startsWith("/app/members/booking-requests") && !location.startsWith("/app/members/vehicles"))
              : location.startsWith(sub.href);
            return (
              <Tooltip key={sub.href}>
                <TooltipTrigger asChild>
                  <Link href={sub.href} className={`group flex items-center gap-3 py-2.5 text-sm cursor-pointer ${sidebarOpen ? (sub.indent ? "pl-9 pr-4" : "px-4") : "justify-center"} ${locked ? "text-gray-400 hover:bg-gray-50" : active ? "text-gray-900 font-medium bg-gray-50" : "text-gray-600 hover:bg-gray-50"}`} data-testid={locked ? `${sub.testid}-locked` : sub.testid} aria-label={locked ? `${sub.label} (locked)` : sub.label}>
                    <SubIcon className="h-4 w-4 shrink-0" />
                    {sidebarOpen && <span className="flex-1">{sub.label}</span>}
                    {sidebarOpen && (locked ? <Lock className="h-3.5 w-3.5 shrink-0 text-amber-500" /> : <FavoriteStar href={sub.href} favorites={favorites} onToggle={toggleFavorite} />)}
                  </Link>
                </TooltipTrigger>
                {!sidebarOpen && !isMobile && <TooltipContent side="right">{sub.label}{locked ? " (locked)" : ""}</TooltipContent>}
              </Tooltip>
            );
          })}

          {/* ─── OPERATIONS GROUP ─── */}
          {showOperations && (
            <>
              {!sidebarOpen && !isMobile ? (
                <Popover>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <PopoverTrigger asChild>
                        <button
                          className={`w-full relative flex items-center justify-center py-2.5 text-sm cursor-pointer ${
                            isOperationsGroupRoute ? "text-gray-900 font-medium bg-gray-50" : "text-gray-600 hover:bg-gray-50"
                          }`}
                          data-testid="button-sidebar-operations"
                          aria-label="Operations navigation group"
                        >
                          <Building2 className="h-4 w-4 shrink-0" />
                          {pendingTaskCount > 0 && (
                            <span className="absolute top-1 right-1 inline-flex items-center justify-center min-w-[14px] h-[14px] text-[9px] font-semibold rounded-full bg-orange-500 text-white px-1">
                              {pendingTaskCount > 99 ? "99+" : pendingTaskCount}
                            </span>
                          )}
                        </button>
                      </PopoverTrigger>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <span className="flex items-center gap-1.5">Operations <ChevronRight className="h-3 w-3" /></span>
                    </TooltipContent>
                  </Tooltip>
                  <PopoverContent side="right" align="start" className="w-52 p-1">
                    <p className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400">Operations</p>
                    {operationsSubItems.filter(s => s.gate()).map((sub) => {
                      const lockState = subItemLockState(sub.href);
                      if (lockState.kind === "hidden") return null;
                      const SubIcon = sub.icon;
                      if (lockState.kind === "locked") {
                        return (
                          <SidebarLockedSubItem
                            key={sub.label}
                            label={sub.label}
                            icon={SubIcon}
                            variant="popover"
                            testid={`link-sidebar-${sub.label.toLowerCase().replace(/\s+/g, "-")}-locked`}
                            href={sub.href}
                          />
                        );
                      }
                      const isSubActive = sub.href === "/app/tasks" ? (location.startsWith("/app/tasks") || location.startsWith("/app/maintenance")) : location === sub.href || location.startsWith(sub.href);
                      if (sub.label === "Supplies" && isTierLocked('Professional')) {
                        return (
                          <button key={sub.label} onClick={() => openUpgrade('Supplies', 'Professional', 'Supplies management requires the Professional plan.', FEATURE_LEARN_MORE_HREF['/app/supplies'])} className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded cursor-pointer text-gray-400" data-testid="link-sidebar-supplies-locked">
                            <SubIcon className="h-3.5 w-3.5" /> {sub.label} <Lock className="h-3 w-3 ml-auto text-amber-500" />
                          </button>
                        );
                      }
                      const comingSoonBadge = comingSoonBadgeForNav(featureGatesByKey, sub.href);
                      return (
                        <Link key={sub.label} href={sub.href}>
                          <span className={`flex items-center gap-2 py-1.5 text-sm rounded cursor-pointer ${sub.indent ? "pl-6 pr-2" : "px-2"} ${isSubActive ? "text-gray-900 font-medium bg-gray-50" : "text-gray-600 hover:bg-gray-50"}`} data-testid={`link-sidebar-${sub.label.toLowerCase().replace(/\s+/g, "-")}`}>
                            <SubIcon className="h-3.5 w-3.5" /> {sub.label}
                            {comingSoonBadge && (
                              <span
                                className="ml-auto inline-block max-w-[96px] truncate rounded-full bg-amber-100 text-amber-700 text-[9px] font-semibold px-1.5 py-0.5 leading-none"
                                title={comingSoonBadge}
                                data-testid={`badge-sidebar-${sub.label.toLowerCase().replace(/\s+/g, "-")}-coming-soon`}
                              >
                                {comingSoonBadge}
                              </span>
                            )}
                          </span>
                        </Link>
                      );
                    })}

                  </PopoverContent>
                </Popover>
              ) : (
                <>
                  <button
                    onClick={() => setOperationsExpanded((v) => !v)}
                    className="w-full flex items-center gap-2 px-4 py-1.5 mt-1 cursor-pointer hover:bg-gray-50"
                    data-testid="button-sidebar-operations"
                  >
                    <Briefcase className="h-3.5 w-3.5 text-gray-400" />
                    <span className="flex-1 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-400">Operations</span>
                    {pendingTaskCount > 0 && !operationsExpanded && (
                      <span className="inline-flex items-center justify-center min-w-[16px] h-[16px] text-[9px] font-semibold rounded-full bg-orange-500 text-white px-1 mr-1">
                        {pendingTaskCount > 99 ? "99+" : pendingTaskCount}
                      </span>
                    )}
                    {operationsExpanded ? <ChevronDown className="h-3 w-3 text-gray-400" /> : <ChevronRight className="h-3 w-3 text-gray-400" />}
                  </button>
                  {operationsExpanded && (
                    <>
                      {operationsSubItems.filter(s => s.gate()).map((sub) => {
                        const lockState = subItemLockState(sub.href);
                        if (lockState.kind === "hidden") return null;
                        const SubIcon = sub.icon;
                        if (lockState.kind === "locked") {
                          return (
                            <SidebarLockedSubItem
                              key={sub.label}
                              label={sub.label}
                              icon={SubIcon}
                              variant="expanded"
                              testid={`link-sidebar-${sub.label.toLowerCase().replace(/\s+/g, "-")}-locked`}
                              href={sub.href}
                            />
                          );
                        }
                        const isSubActive = sub.href === "/app/tasks" ? (location.startsWith("/app/tasks") || location.startsWith("/app/maintenance")) : location === sub.href || location.startsWith(sub.href);
                        if (sub.label === "Supplies" && isTierLocked('Professional')) {
                          return (
                            <button key={sub.label} onClick={() => openUpgrade('Supplies', 'Professional', 'Supplies management requires the Professional plan.', FEATURE_LEARN_MORE_HREF['/app/supplies'])} className="w-full flex items-center gap-3 pl-9 pr-4 py-2 text-sm cursor-pointer text-gray-400" data-testid="link-sidebar-supplies-locked">
                              <SubIcon className="h-4 w-4" />
                              <span className="flex-1 text-left">{sub.label}</span>
                              <Lock className="h-3.5 w-3.5 text-amber-500" />
                            </button>
                          );
                        }
                        const comingSoonBadge = comingSoonBadgeForNav(featureGatesByKey, sub.href);
                        return (
                          <Link key={sub.label} href={sub.href}>
                            <span className={`group flex items-center gap-3 ${sub.indent ? "pl-14" : "pl-9"} pr-2 py-2 text-sm cursor-pointer ${isSubActive ? "text-gray-900 font-medium bg-gray-50" : "text-gray-600 hover:bg-gray-50"}`} data-testid={`link-sidebar-${sub.label.toLowerCase().replace(/\s+/g, "-")}`}>
                              <SubIcon className="h-4 w-4" />
                              <span className="flex-1">{sub.label}</span>
                              {comingSoonBadge ? (
                                <span
                                  className="ml-auto inline-block max-w-[96px] truncate rounded-full bg-amber-100 text-amber-700 text-[9px] font-semibold px-1.5 py-0.5 leading-none"
                                  title={comingSoonBadge}
                                  data-testid={`badge-sidebar-${sub.label.toLowerCase().replace(/\s+/g, "-")}-coming-soon`}
                                >
                                  {comingSoonBadge}
                                </span>
                              ) : (
                                <FavoriteStar href={sub.href} favorites={favorites} onToggle={toggleFavorite} />
                              )}
                            </span>
                          </Link>
                        );
                      })}

                    </>
                  )}
                </>
              )}
            </>
          )}

          {/* ─── FINANCIALS: tabs live on the page, not in the sidebar ─── */}
          {showFinancials && tierAllowsHref('/app/financials') && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Link href="/app/financials" className={`group relative flex items-center gap-3 py-2.5 text-sm cursor-pointer ${sidebarOpen ? "px-4" : "px-0 justify-center"} ${isRevenueGroupRoute ? "text-gray-900 font-medium bg-gray-50" : "text-gray-600 hover:bg-gray-50"}`} data-testid="link-sidebar-financials">
                  <BarChart3 className="h-4 w-4 shrink-0" />
                  {sidebarOpen && <span className="flex-1">Financials</span>}
                  {pendingMailCount > 0 && <span className={`inline-flex items-center justify-center rounded-full bg-blue-600 text-white px-1 text-[10px] font-semibold ${sidebarOpen ? "min-w-[18px] h-[18px]" : "absolute top-1 right-1"}`} data-testid="badge-sidebar-financials-mail" title="Unreviewed financial documents">{pendingMailCount > 99 ? "99+" : pendingMailCount}</span>}
                  {sidebarOpen && <FavoriteStar href="/app/financials" favorites={favorites} onToggle={toggleFavorite} />}
                </Link>
              </TooltipTrigger>
              {!sidebarOpen && !isMobile && <TooltipContent side="right">Financials</TooltipContent>}
            </Tooltip>
          )}

          {/* ─── PORTFOLIO ANALYTICS: direct navigation ─── */}
          {hasPortfolioAnalyticsBeta && (
            <Tooltip>
              <TooltipTrigger asChild>
                {isTierLocked('Professional') ? (
                  <button type="button" onClick={() => openUpgrade('Portfolio Analytics', 'Professional', 'Portfolio Analytics requires the Professional plan.')} className={`w-full flex items-center gap-3 py-2.5 text-sm text-gray-400 ${sidebarOpen ? "px-4" : "justify-center"}`} data-testid="link-sidebar-portfolio-analytics-locked" aria-label="Portfolio Analytics (locked)">
                    <TrendingUp className="h-4 w-4 shrink-0" />
                    {sidebarOpen && <span className="flex-1 text-left">Portfolio Analytics</span>}
                    {sidebarOpen && <Lock className="h-3.5 w-3.5 text-amber-500" />}
                  </button>
                ) : (
                  <Link href="/app/analytics" className={`group flex items-center gap-3 py-2.5 text-sm cursor-pointer ${sidebarOpen ? "px-4" : "justify-center"} ${isAnalyticsRoute ? "text-gray-900 font-medium bg-gray-50" : "text-gray-600 hover:bg-gray-50"}`} data-testid="link-sidebar-portfolio-analytics" aria-label="Portfolio Analytics">
                    <TrendingUp className="h-4 w-4 shrink-0" />
                    {sidebarOpen && <span className="flex-1">Portfolio Analytics</span>}
                    {sidebarOpen && <FavoriteStar href="/app/analytics" favorites={favorites} onToggle={toggleFavorite} />}
                  </Link>
                )}
              </TooltipTrigger>
              {!sidebarOpen && !isMobile && <TooltipContent side="right">Portfolio Analytics{isTierLocked('Professional') ? " (locked)" : ""}</TooltipContent>}
            </Tooltip>
          )}

          {/* ─── DEVICES GROUP ─── */}
          {showDevices && !isSectionNavHidden(featureGatesByKey, "devices") && (
            <>
              {!sidebarOpen && !isMobile ? (
                <Popover>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <PopoverTrigger asChild>
                        <button
                          className={`w-full flex items-center justify-center py-2.5 text-sm cursor-pointer ${
                            isDevicesGroupRoute ? "text-gray-900 font-medium bg-gray-50" : "text-gray-600 hover:bg-gray-50"
                          }`}
                          data-testid="button-sidebar-devices"
                          aria-label="Devices navigation group"
                        >
                          <Wifi className="h-4 w-4 shrink-0" />
                        </button>
                      </PopoverTrigger>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <span className="flex items-center gap-1.5">Devices <ChevronRight className="h-3 w-3" /></span>
                    </TooltipContent>
                  </Tooltip>
                  <PopoverContent side="right" align="start" className="w-52 p-1">
                    <p className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400">Devices</p>
                    {isTierLocked('Professional') ? (
                      <button onClick={() => openUpgrade('Devices', 'Professional', 'Smart locks and thermostats require the Professional plan.')} className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded cursor-pointer text-gray-400" data-testid="link-sidebar-devices-locked">
                        <Wifi className="h-3.5 w-3.5" /> Devices <Lock className="h-3 w-3 ml-auto text-amber-500" />
                      </button>
                    ) : devicesSubItems.filter(d => tierAllowsHref(d.href)).map((sub) => {
                      const SubIcon = sub.icon;
                      const isSubActive = sub.href === "/app/devices" ? location === "/app/devices" : location.startsWith(sub.href);
                      return (
                        <Link key={sub.label} href={sub.href}>
                          <span className={`flex items-center gap-2 px-2 py-1.5 text-sm rounded cursor-pointer ${isSubActive ? "text-gray-900 font-medium bg-gray-50" : "text-gray-600 hover:bg-gray-50"}`} data-testid={`link-sidebar-${sub.label.toLowerCase().replace(/\s+/g, "-")}`}>
                            <SubIcon className="h-3.5 w-3.5" /> {sub.label}
                          </span>
                        </Link>
                      );
                    })}
                  </PopoverContent>
                </Popover>
              ) : (
                <>
                  <button
                    onClick={() => setDevicesExpanded((v) => !v)}
                    className="w-full flex items-center gap-2 px-4 py-1.5 mt-1 cursor-pointer hover:bg-gray-50"
                    data-testid="button-sidebar-devices"
                  >
                    <Wifi className="h-3.5 w-3.5 text-gray-400" />
                    <span className="flex-1 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-400">Devices</span>
                    {devicesExpanded ? <ChevronDown className="h-3 w-3 text-gray-400" /> : <ChevronRight className="h-3 w-3 text-gray-400" />}
                  </button>
                  {devicesExpanded && (
                    <>
                      {isTierLocked('Professional') ? (
                        <button
                          onClick={() => openUpgrade('Devices', 'Professional', 'Smart locks and thermostats require the Professional plan.')}
                          className="w-full flex items-center gap-3 pl-9 pr-4 py-2 text-sm cursor-pointer text-gray-400"
                          data-testid="link-sidebar-devices-locked"
                        >
                          <Wifi className="h-4 w-4" />
                          <span className="flex-1 text-left">Devices</span>
                          <Lock className="h-3.5 w-3.5 text-amber-500" />
                        </button>
                      ) : devicesSubItems.filter(d => tierAllowsHref(d.href)).map((sub) => {
                        const SubIcon = sub.icon;
                        const isSubActive = sub.href === "/app/devices" ? location === "/app/devices" : location.startsWith(sub.href);
                        return (
                          <Link key={sub.label} href={sub.href}>
                            <span className={`group flex items-center gap-3 pl-9 pr-2 py-2 text-sm cursor-pointer ${isSubActive ? "text-gray-900 font-medium bg-gray-50" : "text-gray-600 hover:bg-gray-50"}`} data-testid={`link-sidebar-${sub.label.toLowerCase().replace(/\s+/g, "-")}`}>
                              <SubIcon className="h-4 w-4" />
                              <span className="flex-1">{sub.label}</span>
                              <FavoriteStar href={sub.href} favorites={favorites} onToggle={toggleFavorite} />
                            </span>
                          </Link>
                        );
                      })}
                    </>
                  )}
                </>
              )}
            </>
          )}

          {/* ─── VENDORS GROUP ─── */}
          {showOperations && !isSectionNavHidden(featureGatesByKey, "operations") && (
            <>
              {!sidebarOpen && !isMobile ? (
                <Popover>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <PopoverTrigger asChild>
                        <button
                          className={`w-full flex items-center justify-center py-2.5 text-sm cursor-pointer ${
                            isVendorRoute ? "text-gray-900 font-medium bg-gray-50" : "text-gray-600 hover:bg-gray-50"
                          }`}
                          data-testid="button-sidebar-vendors"
                          aria-label="Vendors navigation group"
                        >
                          <Users className="h-4 w-4 shrink-0" />
                        </button>
                      </PopoverTrigger>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <span className="flex items-center gap-1.5">Vendors <ChevronRight className="h-3 w-3" /></span>
                    </TooltipContent>
                  </Tooltip>
                  <PopoverContent side="right" align="start" className="w-48 p-1">
                    <p className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400">Vendors</p>
                    {vendorSubItems.filter(v => tierAllowsHref(v.href)).map((vsub) => {
                      const VSubIcon = vsub.icon;
                      const isVSubActive = vsub.href === "/app/operations/vendors"
                        ? (location.startsWith("/app/operations/vendors") && !location.includes("/settings"))
                        : vsub.href === "/app/operations/vendors/settings"
                          ? location === "/app/operations/vendors/settings"
                          : location === vsub.href || location.startsWith(vsub.href);
                      return (
                        <Link key={vsub.label} href={vsub.href}>
                          <span className={`flex items-center gap-2 px-2 py-1.5 text-sm rounded cursor-pointer ${isVSubActive ? "text-gray-900 font-medium bg-gray-50" : "text-gray-600 hover:bg-gray-50"}`} data-testid={`link-sidebar-${vsub.label.toLowerCase().replace(/\s+/g, "-")}`}>
                            <VSubIcon className="h-3.5 w-3.5" /> {vsub.label}
                          </span>
                        </Link>
                      );
                    })}
                  </PopoverContent>
                </Popover>
              ) : (
                <>
                  <button
                    onClick={() => setVendorsExpanded((v) => !v)}
                    className="w-full flex items-center gap-2 px-4 py-1.5 mt-1 cursor-pointer hover:bg-gray-50"
                    data-testid="button-sidebar-vendors"
                  >
                    <Users className="h-3.5 w-3.5 text-gray-400" />
                    <span className="flex-1 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-400">Vendors</span>
                    {vendorsExpanded ? <ChevronDown className="h-3 w-3 text-gray-400" /> : <ChevronRight className="h-3 w-3 text-gray-400" />}
                  </button>
                  {vendorsExpanded && vendorSubItems.filter(v => tierAllowsHref(v.href)).map((vsub) => {
                    const VSubIcon = vsub.icon;
                    const isVSubActive = vsub.href === "/app/operations/vendors"
                      ? (location.startsWith("/app/operations/vendors") && !location.includes("/settings"))
                      : vsub.href === "/app/operations/vendors/settings"
                        ? location === "/app/operations/vendors/settings"
                        : location === vsub.href || location.startsWith(vsub.href);
                    return (
                      <Link key={vsub.label} href={vsub.href}>
                        <span className={`group flex items-center gap-3 pl-9 pr-2 py-2 text-sm cursor-pointer ${isVSubActive ? "text-gray-900 font-medium bg-gray-50" : "text-gray-600 hover:bg-gray-50"}`} data-testid={`link-sidebar-${vsub.label.toLowerCase().replace(/\s+/g, "-")}`}>
                          <VSubIcon className="h-4 w-4" />
                          <span className="flex-1">{vsub.label}</span>
                          <FavoriteStar href={vsub.href} favorites={favorites} onToggle={toggleFavorite} />
                        </span>
                      </Link>
                    );
                  })}
                </>
              )}
            </>
          )}

          {/* ─── MARKETING: independent of the Operations dropdown ─── */}
          {showPricing && tierAllowsHref('/app/marketing#section') && (
            <MarketingNavigation sidebarOpen={sidebarOpen} isMobile={isMobile} expanded={marketingExpanded} active={isMarketingRoute} location={location} locked={isTierLocked('Professional')} onToggle={() => setMarketingExpanded(v => !v)} onUpgrade={() => openUpgrade('Marketing', 'Professional', 'Direct Bookings and Third Party Listings require the Professional plan.')} items={marketingSubItems.filter(m => m.gate() && tierAllowsHref(m.href))} renderFavorite={href => <FavoriteStar href={href} favorites={favorites} onToggle={toggleFavorite} />} />
          )}

          {/* ─── OWNERS GROUP ─── */}
          {hasOwnerPortalBeta && !isTierLocked("PMPro") && (
            <>
              {!sidebarOpen && !isMobile ? (
                <Popover>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <PopoverTrigger asChild>
                        <button
                          className={`w-full flex items-center justify-center py-2.5 text-sm cursor-pointer ${
                            isOwnersGroupRoute ? "text-gray-900 font-medium bg-gray-50" : "text-gray-600 hover:bg-gray-50"
                          }`}
                          data-testid="button-sidebar-owners"
                          aria-label="Owners navigation group"
                        >
                          <Handshake className="h-4 w-4 shrink-0" />
                        </button>
                      </PopoverTrigger>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <span className="flex items-center gap-1.5">Owners <ChevronRight className="h-3 w-3" /></span>
                    </TooltipContent>
                  </Tooltip>
                  <PopoverContent side="right" align="start" className="w-52 p-1">
                    <p className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400">Owners</p>
                    {ownersSubItems.map((osub) => {
                      const OSubIcon = osub.icon;
                      const isOSubActive = osub.href === "/app/operations/owners"
                        ? location.startsWith("/app/operations/owners") && !location.startsWith("/app/operations/owner-invoices")
                        : location === osub.href || location.startsWith(osub.href);
                      return (
                        <Link key={osub.href} href={osub.href}>
                          <span className={`flex items-center gap-2 px-2 py-1.5 text-sm rounded cursor-pointer ${isOSubActive ? "text-gray-900 font-medium bg-gray-50" : "text-gray-600 hover:bg-gray-50"}`} data-testid={osub.testid}>
                            <OSubIcon className="h-3.5 w-3.5" /> {osub.label}
                          </span>
                        </Link>
                      );
                    })}
                  </PopoverContent>
                </Popover>
              ) : (
                <>
                  <button
                    onClick={() => setOwnersExpanded((v) => !v)}
                    className="w-full flex items-center gap-2 px-4 py-1.5 mt-1 cursor-pointer hover:bg-gray-50"
                    data-testid="button-sidebar-owners"
                  >
                    <Handshake className="h-3.5 w-3.5 text-gray-400" />
                    <span className="flex-1 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-400">Owners</span>
                    {ownersExpanded ? <ChevronDown className="h-3 w-3 text-gray-400" /> : <ChevronRight className="h-3 w-3 text-gray-400" />}
                  </button>
                  {ownersExpanded && ownersSubItems.map((osub) => {
                    const OSubIcon = osub.icon;
                    const isOSubActive = osub.href === "/app/operations/owners"
                      ? location.startsWith("/app/operations/owners") && !location.startsWith("/app/operations/owner-invoices")
                      : location === osub.href || location.startsWith(osub.href);
                    return (
                      <Link key={osub.href} href={osub.href}>
                        <span className={`group flex items-center gap-3 pl-9 pr-2 py-2 text-sm cursor-pointer ${isOSubActive ? "text-gray-900 font-medium bg-gray-50" : "text-gray-600 hover:bg-gray-50"}`} data-testid={osub.testid}>
                          <OSubIcon className="h-4 w-4" />
                          <span className="flex-1">{osub.label}</span>
                          <FavoriteStar href={osub.href} favorites={favorites} onToggle={toggleFavorite} />
                        </span>
                      </Link>
                    );
                  })}
                </>
              )}
            </>
          )}

          {/* ─── FOOTER UTILITIES ─── */}
          <div className="border-t border-gray-200 my-2" />

          {showTeamPage && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Link href="/app/team">
                  <span className={`group flex items-center gap-3 py-2 text-sm cursor-pointer ${sidebarOpen ? "px-4" : "px-0 justify-center"} ${location.startsWith("/app/team") && !location.startsWith("/app/team-stats") ? "text-gray-900 font-medium bg-gray-50" : "text-gray-600 hover:bg-gray-50"}`} data-testid="link-sidebar-team">
                    <Users className="h-4 w-4 shrink-0" />
                    {sidebarOpen && <><span className="flex-1">My Team</span><FavoriteStar href="/app/team" favorites={favorites} onToggle={toggleFavorite} /></>}
                  </span>
                </Link>
              </TooltipTrigger>
              {!sidebarOpen && !isMobile && <TooltipContent side="right">My Team</TooltipContent>}
            </Tooltip>
          )}

          {/* Team Stats — submenu row under My Team, visible to every user on
              every tier. The page itself (and its API) still restricts DATA to
              account owners and admin-role teammates; regular members see an
              "admins only" state when they open it. */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Link href="/app/team-stats">
                <span className={`group flex items-center gap-3 py-2 text-sm cursor-pointer ${sidebarOpen ? "pl-9 pr-4" : "px-0 justify-center"} ${location.startsWith("/app/team-stats") ? "text-gray-900 font-medium bg-gray-50" : "text-gray-600 hover:bg-gray-50"}`} data-testid="link-sidebar-team-stats">
                  <BarChart3 className="h-4 w-4 shrink-0" />
                  {sidebarOpen && <><span className="flex-1">Team Stats</span><FavoriteStar href="/app/team-stats" favorites={favorites} onToggle={toggleFavorite} /></>}
                </span>
              </Link>
            </TooltipTrigger>
            {!sidebarOpen && !isMobile && <TooltipContent side="right">Team Stats</TooltipContent>}
          </Tooltip>

          {showBilling && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Link href="/app/account/billing">
                  <span className={`group flex items-center gap-3 py-2 text-sm cursor-pointer ${sidebarOpen ? "px-4" : "px-0 justify-center"} ${location.startsWith("/app/account/billing") && !location.startsWith("/app/account/billing-") && location !== "/app/account/pm-billing" ? "text-gray-900 font-medium bg-gray-50" : "text-gray-600 hover:bg-gray-50"}`} data-testid="link-sidebar-billing">
                    <Wallet className="h-4 w-4 shrink-0" />
                    {sidebarOpen && <><span className="flex-1">Billing</span><FavoriteStar href="/app/account/billing" favorites={favorites} onToggle={toggleFavorite} /></>}
                  </span>
                </Link>
              </TooltipTrigger>
              {!sidebarOpen && !isMobile && <TooltipContent side="right">Billing</TooltipContent>}
            </Tooltip>
          )}


          {showPadsplitSync && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Link href="/app/padsplit-sync">
                  <span className={`group flex items-center gap-3 py-2 text-sm cursor-pointer ${sidebarOpen ? "px-4" : "px-0 justify-center"} ${location.startsWith("/app/padsplit-sync") ? "text-gray-900 font-medium bg-gray-50" : "text-gray-600 hover:bg-gray-50"}`} data-testid="link-sidebar-padsplit-sync">
                    <Zap className="h-4 w-4 shrink-0" />
                    {sidebarOpen && <><span className="flex-1">PadSplit Sync</span><FavoriteStar href="/app/padsplit-sync" favorites={favorites} onToggle={toggleFavorite} /></>}
                  </span>
                </Link>
              </TooltipTrigger>
              {!sidebarOpen && !isMobile && <TooltipContent side="right">PadSplit Sync</TooltipContent>}
            </Tooltip>
          )}

          {showOperations && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Link href="/app/account/knowledge-base">
                  <span className={`group flex items-center gap-3 py-2 text-sm cursor-pointer ${sidebarOpen ? "px-4" : "px-0 justify-center"} ${location.startsWith("/app/account/knowledge-base") ? "text-gray-900 font-medium bg-gray-50" : "text-gray-600 hover:bg-gray-50"}`} data-testid="link-sidebar-global-kb">
                    <BookOpen className="h-4 w-4 shrink-0" />
                    {sidebarOpen && <><span className="flex-1">Knowledge Base</span><FavoriteStar href="/app/account/knowledge-base" favorites={favorites} onToggle={toggleFavorite} /></>}
                  </span>
                </Link>
              </TooltipTrigger>
              {!sidebarOpen && !isMobile && <TooltipContent side="right">Knowledge Base</TooltipContent>}
            </Tooltip>
          )}

          {userQuery.data?.user?.isAdmin && !userQuery.data?.user?.superAdmin && (
            <>
              <div className="border-t border-gray-200 my-2" />
              {sidebarOpen && (
                <p className="px-4 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wide">Platform Admin</p>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link href="/app/admin/dashboard-layouts">
                    <span className={`flex items-center gap-3 py-2.5 text-sm cursor-pointer ${sidebarOpen ? "px-4" : "px-0 justify-center"} ${location.startsWith("/app/admin/dashboard-layouts") ? "text-amber-700 font-medium bg-amber-50" : "text-amber-600 hover:bg-amber-50"}`} data-testid="link-sidebar-app-admin-dashboard-layouts">
                      <LayoutGrid className="h-4 w-4 shrink-0" />
                      {sidebarOpen && "Dashboard Layouts"}
                    </span>
                  </Link>
                </TooltipTrigger>
                {!sidebarOpen && !isMobile && <TooltipContent side="right">Dashboard Layouts</TooltipContent>}
              </Tooltip>
            </>
          )}

          {(betaToolAccess.allowed || userQuery.data?.user?.superAdmin || userQuery.data?.user?.adminPermissions?.includes("impersonate_users")) && (
            <>
              <div className="border-t border-gray-200 my-2" />
              {sidebarOpen && (
                <p className="px-4 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wide">Developer Tools</p>
              )}
              {betaToolAccess.allowed && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link href="/app/admin/beta-features">
                    <span className={`flex items-center gap-3 py-2.5 text-sm cursor-pointer ${sidebarOpen ? "px-4" : "px-0 justify-center"} ${location.startsWith("/app/admin/beta-features") ? "text-amber-700 font-medium bg-amber-50" : "text-amber-600 hover:bg-amber-50"}`} data-testid="link-sidebar-app-admin-beta-features">
                      <Rocket className="h-4 w-4 shrink-0" />
                      {sidebarOpen && "Beta Access"}
                    </span>
                  </Link>
                </TooltipTrigger>
                {!sidebarOpen && !isMobile && <TooltipContent side="right">Beta Access</TooltipContent>}
              </Tooltip>
              )}
              {(userQuery.data?.user?.superAdmin || userQuery.data?.user?.adminPermissions?.includes("impersonate_users")) && <Tooltip>
                <TooltipTrigger asChild>
                  <Link href="/app/dev-tools/impersonate">
                    <span className={`flex items-center gap-3 py-2.5 text-sm cursor-pointer ${sidebarOpen ? "px-4" : "px-0 justify-center"} ${location.startsWith("/app/dev-tools/impersonate") ? "text-amber-700 font-medium bg-amber-50" : "text-amber-600 hover:bg-amber-50"}`} data-testid="link-sidebar-dev-tools-impersonate">
                      <Code2 className="h-4 w-4 shrink-0" />
                      {sidebarOpen && "Impersonate User"}
                    </span>
                  </Link>
                </TooltipTrigger>
                {!sidebarOpen && !isMobile && <TooltipContent side="right">Impersonate User</TooltipContent>}
              </Tooltip>}
              {userQuery.data?.user?.superAdmin && (
              <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link href="/app/dev-tools/log">
                    <span className={`flex items-center gap-3 py-2.5 text-sm cursor-pointer ${sidebarOpen ? "px-4" : "px-0 justify-center"} ${location.startsWith("/app/dev-tools/log") ? "text-amber-700 font-medium bg-amber-50" : "text-amber-600 hover:bg-amber-50"}`} data-testid="link-sidebar-dev-tools-log">
                      <ScrollText className="h-4 w-4 shrink-0" />
                      {sidebarOpen && "Impersonation Log"}
                    </span>
                  </Link>
                </TooltipTrigger>
                {!sidebarOpen && !isMobile && <TooltipContent side="right">Impersonation Log</TooltipContent>}
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link href="/app/dev-tools/ai-playground">
                    <span className={`flex items-center gap-3 py-2.5 text-sm cursor-pointer ${sidebarOpen ? "px-4" : "px-0 justify-center"} ${location.startsWith("/app/dev-tools/ai-playground") ? "text-amber-700 font-medium bg-amber-50" : "text-amber-600 hover:bg-amber-50"}`} data-testid="link-sidebar-dev-tools-ai-playground">
                      <FlaskConical className="h-4 w-4 shrink-0" />
                      {sidebarOpen && "AI Suggest Playground"}
                    </span>
                  </Link>
                </TooltipTrigger>
                {!sidebarOpen && !isMobile && <TooltipContent side="right">AI Suggest Playground</TooltipContent>}
              </Tooltip>
              </>
              )}
            </>
          )}


          {tierAllowsHref('/app/faq') && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Link href="/app/faq">
                <span className={`group flex items-center gap-3 py-2 text-sm cursor-pointer ${sidebarOpen ? "px-4" : "px-0 justify-center"} ${location.startsWith("/app/faq") ? "text-gray-900 font-medium bg-gray-50" : "text-gray-600 hover:bg-gray-50"}`} data-testid="link-sidebar-faq">
                  <HelpCircle className="h-4 w-4 shrink-0" />
                  {sidebarOpen && <span className="flex-1">Help / FAQ</span>}
                </span>
              </Link>
            </TooltipTrigger>
            {!sidebarOpen && !isMobile && <TooltipContent side="right">Help / FAQ</TooltipContent>}
          </Tooltip>
          )}

          {visibleResourceItems.length > 0 && (!sidebarOpen && !isMobile ? (
            <Popover>
              <Tooltip>
                <TooltipTrigger asChild>
                  <PopoverTrigger asChild>
                    <button type="button" className={`flex w-full justify-center py-2.5 ${isResourcesRoute ? "text-gray-900 bg-gray-50" : "text-gray-600 hover:bg-gray-50"}`} data-testid="button-sidebar-resources" aria-label="Resources">
                      <BookOpen className="h-4 w-4" />
                    </button>
                  </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent side="right">Resources</TooltipContent>
              </Tooltip>
              <PopoverContent side="right" align="start" className="w-52 p-1">
                <p className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400">Resources</p>
                {visibleResourceItems.map((item) => {
                  const Icon = item.icon;
                  const active = location.startsWith(item.href);
                  return <Link key={item.href} href={item.href}><span className={`relative flex items-center gap-2 px-2 py-1.5 text-sm rounded ${item.highlighted ? "text-brand font-semibold bg-brand-subtle hover:bg-brand-muted" : active ? "text-gray-900 font-medium bg-gray-50" : "text-gray-600 hover:bg-gray-50"}`} data-testid={item.testid}><Icon className="h-3.5 w-3.5" />{item.label}{item.href === "/app/connections" && connectionsUnreadCount > 0 && <span className="ml-auto inline-flex min-w-[18px] h-4 items-center justify-center rounded-full bg-blue-600 px-1 text-[9px] font-semibold text-white" data-testid="badge-connections-unread">{connectionsUnreadCount > 99 ? "99+" : connectionsUnreadCount}</span>}</span></Link>;
                })}
              </PopoverContent>
            </Popover>
          ) : (
            <>
              <button type="button" onClick={() => setResourcesExpanded((value) => !value)} className={`w-full flex items-center gap-2 px-4 py-1.5 mt-1 hover:bg-gray-50 ${isResourcesRoute ? "bg-gray-50" : ""}`} data-testid="button-sidebar-resources" aria-expanded={resourcesExpanded}>
                <BookOpen className="h-3.5 w-3.5 text-gray-400" />
                <span className="flex-1 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-400">Resources</span>
                {resourcesExpanded ? <ChevronDown className="h-3 w-3 text-gray-400" /> : <ChevronRight className="h-3 w-3 text-gray-400" />}
              </button>
              {resourcesExpanded && visibleResourceItems.map((item) => {
                const Icon = item.icon;
                const active = location.startsWith(item.href);
                return (
                  <Link key={item.href} href={item.href}>
                    <span className={`group relative flex items-center gap-3 pl-9 pr-2 py-2 text-sm cursor-pointer ${item.highlighted ? "text-brand font-semibold bg-brand-subtle hover:bg-brand-muted" : active ? "text-gray-900 font-medium bg-gray-50" : "text-gray-600 hover:bg-gray-50"}`} data-testid={item.testid}>
                      <Icon className="h-4 w-4" />
                      <span className="flex-1">{item.label}</span>
                      {item.href === "/app/connections" && sidebarOpen && connectionsUnreadCount > 0 && <span className="inline-flex min-w-[20px] h-5 items-center justify-center rounded-full bg-blue-600 px-1.5 text-[10px] font-semibold text-white" data-testid="badge-connections-unread">{connectionsUnreadCount > 99 ? "99+" : connectionsUnreadCount}</span>}
                      {item.href === "/app/connections" && !sidebarOpen && connectionsUnreadCount > 0 && <span className="absolute top-1 right-1 inline-flex min-w-[14px] h-[14px] items-center justify-center rounded-full bg-blue-600 px-1 text-[9px] font-semibold text-white" data-testid="badge-connections-unread-collapsed">{connectionsUnreadCount > 99 ? "99+" : connectionsUnreadCount}</span>}
                      <FavoriteStar href={item.href} favorites={favorites} onToggle={toggleFavorite} />
                    </span>
                  </Link>
                );
              })}
            </>
          ))}

          <FeatureRequestDialog trigger={<button type="button" className={`group flex items-center gap-3 py-2 text-sm cursor-pointer text-gray-600 hover:bg-gray-50 ${sidebarOpen ? "px-4" : "px-0 justify-center"}`} data-testid="button-request-feature" aria-label="Request a feature"><Lightbulb className="h-4 w-4 shrink-0" />{sidebarOpen && <span className="flex-1 text-left">Request a feature</span>}</button>} />

          </TooltipProvider>
        </nav>
      </aside>

      <div className="h-full flex-1 min-h-0 min-w-0 flex flex-col overflow-hidden">
        <div className="shrink-0 flex items-center">
          {isMobile && !sidebarOpen && (
            <button onClick={() => setSidebarOpen(true)} className="p-2 ml-2 rounded hover:bg-gray-100 text-gray-500" data-testid="button-open-sidebar">
              <Menu className="h-5 w-5" />
            </button>
          )}
          {!isMobile && !sidebarOpen && (
            <Link href="/app/dashboard" className="pl-3 shrink-0">
              <img src={logoImg} alt="PadPulse" className="h-[32px] w-auto cursor-pointer" data-testid="link-topbar-logo" />
            </Link>
          )}
          <div className="flex-1">
            <NotificationHeader user={user} planLabel={effectivePlan} planInherited={planInherited} planInviterName={planInviterName} inheritedFounderTier={inheritedFounderTier} inheritedFounderStatus={inheritedFounderStatus} onLogout={() => logoutMutation.mutate()} />
          </div>
        </div>

        <StagingBanner />
        <FinishSetupBanner failedSeeds={starterSeedFailedSeeds} />
        <PromiseTrustBanner />
        {user && !user.isAdmin && <V3BillingBanner />}
        <PrivacyPolicyNotice />
        <ImpersonationGuardProvider>
          <main className={`flex-1 min-h-0 ${scrollLocked ? "overflow-hidden" : "overflow-auto overscroll-contain"}`} data-testid="app-main">
            <PermissionsProvider enabled={!!user}>
              {user && !user.isAdmin && !(ownInactiveReason && !isAvailableWhileInactive(location)) && (
                <SubscriptionInactiveBanner
                  ownVariant={ownInactiveReason}
                  fallbackVariant={!isBillableAccountOwner ? "team_member" : "generic"}
                />
              )}
              {(() => {
                // Task #3976 — route-level gate overlay. Fail-open while the
                // gate query is still cold-loading (the server still blocks the
                // underlying API). coming_soon → overlay with the admin message;
                // hidden → render the 404 page, as if the route did not exist.
                if (featureGatesLoaded) {
                  const gateBlock = gateBlockForRoute(featureGatesByKey, location);
                  if (gateBlock) {
                    return gateBlock.mode === "coming_soon" ? (
                      <ComingSoon message={gateBlock.message} />
                    ) : (
                      <NotFoundPage />
                    );
                  }
                }
                // Lapsed billing (payment failed / trial over / subscription
                // ended): gated pages get a clear explanation + CTA instead of
                // rendering half-broken on 402s. The free P&L and the
                // account/billing pages stay usable.
                if (ownInactiveReason && !isAvailableWhileInactive(location)) {
                  return <SubscriptionInactiveScreen variant={ownInactiveReason} />;
                }
                // Route-level TIER-lock overlay: when the current route resolves
                // to a denied menu-tier-lock key, render the real target page
                // blurred + non-interactive behind an upgrade card so the user
                // can preview what's gated. Fail-open while the denied query is
                // still loading (same as the admin gate above; the sidebar lock
                // visuals still steer users correctly in the meantime).
                if (tierLocksQuery.isSuccess) {
                  const lock = routeTierLock(location);
                  if (lock) {
                    return (
                      <div className="relative min-h-full" data-testid="overlay-tier-lock-route">
                        <div
                          className="pointer-events-none select-none blur-sm opacity-50"
                          aria-hidden="true"
                        >
                          {children}
                        </div>
                        <div className="absolute inset-0 z-10 flex items-start justify-center overflow-auto bg-background/40 p-4 sm:p-8">
                          <div className="mt-6 w-full sm:max-w-md rounded-lg border bg-background p-6 shadow-xl">
                            <UpgradeContent
                              feature={lock.label}
                              requiredTier={lock.tier}
                              reason={`${lock.label} requires the ${planDisplayName(lock.tier)} plan.`}
                              learnMoreHref={lock.learnMoreHref}
                              onDismiss={() => navigate("/app/dashboard")}
                              dismissLabel="Back to dashboard"
                            />
                          </div>
                        </div>
                      </div>
                    );
                  }
                }
                return children;
              })()}
            </PermissionsProvider>
          </main>
        </ImpersonationGuardProvider>
      </div>
      {user && (
        <BirdieWidget
          userId={user.id}
          email={user.email}
          name={user.fullName ?? user.name}
        />
      )}
      {/* Floating activation checklist — bottom-LEFT (Birdie/Crisp own the
          right corner); z-30 so the mobile sidebar drawer (z-40) covers it. */}
      <UpgradeModal
        open={upgradeModal.open}
        onOpenChange={(open) => setUpgradeModal(prev => ({ ...prev, open }))}
        feature={upgradeModal.feature}
        requiredTier={upgradeModal.requiredTier}
        reason={upgradeModal.reason}
        learnMoreHref={upgradeModal.learnMoreHref}
      />
      {/* Task #4436 — reactive upgrade prompt for 403 UPGRADE_REQUIRED API
          responses (deep link / stale tab). Suppressed when the route-level
          tier-lock overlay is already showing the upgrade card for this page so
          a known-locked deep link doesn't double-prompt. */}
      <UpgradeRequiredListener suppressed={!!routeTierLock(location)} />
      {/* v3 billing 402s: ACCOUNT_READ_ONLY → add a card;
          HOST_TRIAL_REQUIRED / RESEARCH_CAP → start the 14-day Host trial. */}
      <V3BillingRequiredListener />
    </div>
  );
}
